import React, { useState, useEffect } from "react";
import { Send, PackageMinus, PackagePlus, ArrowRightLeft } from "lucide-react";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";
import axios from "axios";
import moment from "moment";
import { useRNDetail } from "../hooks/useRNDetail";
import { useDocumentStatusFields } from "../hooks/useDocumentStatusFields";
import { RNParamEntry, RNRazmerDispatchStage, RNWorkOrderDoc, useRNWorkOrder } from "../hooks/useRNWorkOrder";
import { usePermissions } from "../context/PermissionContext";
import { useAuth } from "../context/AuthContext";
import ModalSignPin from "../modals/ModalSignPin";
import { useRNWorkOrderList, RNWorkOrderRow } from "../hooks/useRNWorkOrderList";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import RNMasterHeader from "../components/RNMasterHeader";
import OSDetailHeader from "../components/OSDetailHeader";
import { OS_CARD_HEADER_BUTTON_CLS, OS_CARD_HEADER_BUTTON_ACTIVE_CLS } from "../components/OSCard";
import { useSubprocesses } from "../select/SubprocessCells";
import ModalConfirmation from "../modals/ModalConfirmation";
import UnsavedChangesModal from "../components/UnsavedChangesModal";
import { Ingredient, Item } from "../types/Types";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../assets/LoadingSpinner";
import ErrorComponent from "../assets/ErrorComponent";
import { ItemCacheProvider } from '../context/ItemCacheContext';
import { useItemCache } from '../context/ItemCacheContext';
import ShortcutNavBar from "../components/ShortcutNavBar";
import ExcelExportDropdown from "../components/ExcelExportDropdown";
import RNExportFilterModal from "../components/RNExportFilterModal";
import { useToast } from "../context/ToastContext";
import { buildRazmerCopySources } from "../utils/rnMaterialnoCopySource";
import OSStageTree, { RAZMER_STAGE_ID } from "../components/OSStageTree";
import OSStagePanel from "../components/OSStagePanel";
import { RazmerRow, emptyRazmerRow } from "../components/OSRazmerTable";
import {
  useRNRazmerAutosave,
  keepRazmerLocalEdits,
  razmerAutosaveCellKey,
  RazmerCellSaveStatusMap,
} from "../hooks/useRNRazmerAutosave";
import useStageAutosave from "../hooks/useStageAutosave";
import AutosaveStatusPill from "../components/AutosaveStatusPill";
// Иста табела како на /os — намерно ЕДНА. РН немаше причина за свој близнак: копијата
// (RNMaterialnoTable, 341 реда) единствено се разликуваше по тоа што дозволуваше куцање иденти
// и бришење редови, а тоа е токму она што не смее — редовите се изведени од рецептот.
import { MatGroup, emptyGroup } from "../components/OSMaterialnoTable";
import OSProductivityTable, { ProdRow, emptyProdRow } from "../components/OSProductivityTable";
import RNStatusLogTable from "../components/RNStatusLogTable";
import { SubprocessItemRow, emptySubprocessItemRow, deriveIssueSubprocessIds } from "../components/OSSubprocessItemsTable";
import { mapRazmerRows, mapRNReceiptRows, mapMaterialnoGroups, mapProductivityRows, mapParamValueMap } from "../utils/osIngredientMappers";
import { useProductionStatusOptions } from "../select/ProductionStatusSelect";
import RNRazmerDispatchModal from "../components/RNRazmerDispatchModal";
import RNRasturStageModal from "../components/RNRasturStageModal";
import { parseDecimalInput } from "../utils/decimalInput";
import { formatRNDocumentError } from "../utils/rnDocumentError";
import { useRNViewerScope } from "../hooks/useRNViewerScope";
import { suggestFinishedBatch, suggestFinishedExpiry, suggestInterProductBatch, suggestInterProductExpiry } from "../utils/batchRules";
import { WorkOrderRole } from "../types/Types";
import { isIntermediateOrSemi, isFinishedProduct } from "../utils/accountingTypeGroup";
import { isRowVerified } from "../utils/rnRowVerification";
import { roleOfRef, receiptEntryRole, currentDocTypeCode, entryHasCode } from "../utils/workOrderRole";
import RNSubprocessMachinesTable from '../components/RNSubprocessMachinesTable';
import RNConfiguredDocumentActionButton from "../components/RNConfiguredDocumentActionButton";
import { useRNMachineRepairs } from '../hooks/useRNMachineRepairs';
import { toRNMachineRepairsPayload } from '../types/rnMachineRepairs';
import { useRazmerHiddenColumns } from '../hooks/useRazmerHiddenColumns';
import { useRNProstDocumentActions } from '../hooks/useRNProstDocumentActions';
import { useRNRazmerLots } from '../hooks/useRNRazmerLots';
import { useRNWorkOrderActuals } from '../hooks/useRNWorkOrderActuals';
import { useRNRazmerDispatch } from '../hooks/useRNRazmerDispatch';
import { buildProcessGroupOrder } from '../utils/processGroupOrder';
import { useRNMaterialDocumentActions } from '../hooks/useRNMaterialDocumentActions';
import { useRNMaterialnoSeeding } from '../hooks/useRNMaterialnoSeeding';
import { useRNMachineRepairApproval } from '../hooks/useRNMachineRepairApproval';
import { useRNProductivityPlan } from '../hooks/useRNProductivityPlan';
import { useRNIngredientExcelExport } from '../hooks/useRNIngredientExcelExport';
import { useRNConfiguredDocumentActions } from '../hooks/useRNConfiguredDocumentActions';

interface RNWorkOrderInnerProps {
  /** 'simple' = RN-PROST (document pattern for 6010-style simple work orders): only the
   *  master header, Готов Производ card + Податоци panel, and the Размерување panel are
   *  rendered — no stage tab strip, no Log Book / total productivity toggles, no
   *  Меѓу/Полупроизвод table. Everything else in this screen (state, hooks, save/dispatch
   *  logic) is identical to the full 'RN' pattern; only JSX visibility is gated. */
  variant?: 'simple';
}

// RN persists actuals to /api/rn-work-orders; OS reference tables stay read-only and
// are NOT written back. RN never mutates /api/ingredients.
const RNWorkOrderInner: React.FC<RNWorkOrderInnerProps> = ({ variant }) => {
  // RN-PROST: forces the screen onto the Размерување panel only, with no stage tabs to
  // navigate away from it. See the Props doc comment above for the full list of hidden
  // sections.
  const isSimpleVariant = variant === 'simple';
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { isAdmin, hasWrite } = usePermissions();
  const { documentTypes } = useDocumentTypesContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { code: workOrderDocumentCode } = useParams<{ code?: string }>();
  const rnDocumentCode = workOrderDocumentCode || "6020";
  const machineRepairNavigation = (location.state as {
    machineRepairRequest?: {
      machineRepairRequestId?: string;
      subprocessId?: string;
      machineId?: string;
    };
  } | null)?.machineRepairRequest;
  const { getItem, addItemsToCache, fetchItemById } = useItemCache();
  const { statusFields } = useDocumentStatusFields(rnDocumentCode);
  // Module-cached — safe to call here; SubprocessCells already calls it, no duplicate fetch
  const subprocesses = useSubprocesses();
  const subprocessesRef = React.useRef(subprocesses);
  subprocessesRef.current = subprocesses;
  const productionStatusOptions = useProductionStatusOptions();
  // RN-PROST (6010) only: Размерување Column Manager — see OSStagePanel's
  // razmerColumnManager prop below. No-op state on the full RN pattern.
  const { hiddenColumns: razmerHiddenColumns, handleColumnToggle: handleRazmerColumnToggle, handleColumnDrop: handleRazmerColumnDrop } = useRazmerHiddenColumns();

  // Left vertical stage tree — groups/stages driven by process-definitions / subprocesses codebooks.
  // RN-PROST pins this to Размерување from the start: OSStageTree (which normally defaults an
  // empty activeStage to RAZMER_STAGE_ID on mount) is never rendered in simple mode, so nothing
  // else would set it.
  const [activeStage, setActiveStage] = useState<string>(() => (isSimpleVariant ? RAZMER_STAGE_ID : ''));
  useEffect(() => {
    if (machineRepairNavigation?.subprocessId && subprocesses.some((sp) => sp._id === machineRepairNavigation.subprocessId)) {
      setActiveStage(machineRepairNavigation.subprocessId);
    }
  }, [machineRepairNavigation?.subprocessId, subprocesses]);
  // Per-ident selected subprocesses from OS, used to filter the left stage tree.
  const [selectedSubprocessIds, setSelectedSubprocessIds] = useState<string[]>([]);
  // Per-ident Продуктивност rows (persisted). Starts with 1 row; ArrowDown adds more.
  const [productivityRows, setProductivityRows] = useState<ProdRow[]>(() => [emptyProdRow()]);
  const handleSaveRef = React.useRef<() => Promise<void>>(async () => {});
  // Preview-only: a Razmer start maps to the first globally ordered production status.
  // Kept by current ident only; this is intentionally not a persistent status-log event.
  const [razmerStartedStatus, setRazmerStartedStatus] = useState<{
    itemId: string;
    workOrderId: string;
    statusId: string;
    statusIndex: number;
    stageId: string;
  } | null>(null);
  // OS master-recipe productivity plan (per subprocess) — drives the read-only РН rows.
  const [osProductivity, setOsProductivity] = useState<ProdRow[]>([]);
  // Per-ident Размерување rows (persisted)
  const [razmerRows, setRazmerRows] = useState<RazmerRow[]>(() => [emptyRazmerRow()]);
  // Latest rows for the quiet autosave merge (what the operator typed while a PUT ran).
  const razmerRowsRef = React.useRef(razmerRows);
  razmerRowsRef.current = razmerRows;
  // Serialises every RN save (toolbar, ✓ confirm, timing commit, cell autosave): the next
  // one starts only after the previous PUT settled — never two writes to one RN at once.
  const saveChainRef = React.useRef<Promise<unknown>>(Promise.resolve());

  // Per-ident Материјално groups (persisted)
  const [materialnoGroups, setMaterialnoGroups] = useState<MatGroup[]>(() => [emptyGroup(0, "1")]);
  // Owns razmer/materialno/interProduct actuals state + refs, and the merge/build
  // helpers that map them onto and off of the OS-shaped row state below.
  const {
    razmerActualRowsRef,
    materialnoActualRowsRef,
    interProductActualRowsRef,
    mergeRazmerActuals,
    setRazmerActuals,
    mergeMaterialnoActuals,
    setMaterialnoActuals,
    toRazmerActualRows,
    toMaterialnoActualRows,
    mergeInterProductActuals,
    setInterProductActuals,
    toInterProductActualRows,
  } = useRNWorkOrderActuals();
  // Претходниот render-ов сет `subprocessId|itemId` seed клучеви (Размерување + Меѓу/Полупроизвод)
  // — влез за un-seeding effect-от подолу. Се ресетира на null при секое вчитување на друг запис,
  // за да не се diff-ираат клучевите на излезниот запис со влезниот.
  const prevSeedKeysRef = React.useRef<Set<string> | null>(null);
  // Per-ident Меѓу/Полупроизвод rows (refs + quantity; ident/name/ЕМ derived from item)
  const [subprocessItemRows, setSubprocessItemRows] = useState<SubprocessItemRow[]>(() => [emptySubprocessItemRow()]);
  // Kept separately from the displayed rows so a late subprocess-codebook load can
  // resolve the no-intermediate finished receipt to the same final visible stage.
  const receiptSourceRef = React.useRef<any>(null);
  // InProcess values keyed by Subprocess.inProcess entry _id; persisted per ident.
  const [inProcessValues, setInProcessValues] = useState<Record<string, string>>({});
  // Параметри за монтажа values keyed by Subprocess.montageParams entry _id; persisted per ident.
  const [montageValues, setMontageValues] = useState<Record<string, string>>({});
  // РН-ските СВОИ InProcess / монтажни читања, држани и во ref. Нормативот дава
  // спецификација за истите записи, а syncIngredientFields ја впишува кога ќе се
  // вчита OS-от — па РН вредностите се пре-применуваат врз неа од овде, за да не
  // зависи резултатот од тоа кој effect ќе слета прв (истиот образец како
  // razmerActualRowsRef погоре).
  const rnParamValuesRef = React.useRef<{ inProcess: Record<string, string>; montage: Record<string, string> }>({ inProcess: {}, montage: {} });
  const { machineRepairs, setMachineRepairs, clearMachineRepairs, updateMachineRepair } = useRNMachineRepairs();

  const setRnParamValues = React.useCallback((inProcess: Record<string, string>, montage: Record<string, string>) => {
    rnParamValuesRef.current = { inProcess, montage };
    setInProcessValues((prev) => ({ ...prev, ...inProcess }));
    setMontageValues((prev) => ({ ...prev, ...montage }));
  }, []);

  // entry _id -> owning subprocess _id, across both codebook arrays, so a saved value
  // knows which subprocess it belongs to
  const entryOwner = React.useMemo(() => {
    const map: Record<string, string> = {};
    subprocesses.forEach((sp) => {
      (sp.inProcess || []).forEach((e) => { map[e._id] = sp._id; });
      (sp.montageParams || []).forEach((e) => { map[e._id] = sp._id; });
    });
    return map;
  }, [subprocesses]);

  const toParamEntries = React.useCallback((values: Record<string, string>): RNParamEntry[] =>
    Object.entries(values)
      .filter(([entryId, value]) => value.trim() !== "" && selectedSubprocessIds.includes(entryOwner[entryId]))
      .map(([entryId, value]) => ({ subprocessId: entryOwner[entryId], entryId, value })),
    [entryOwner, selectedSubprocessIds]
  );

  // Dirty tracking for unsaved changes
  const [isDirty, setIsDirty] = useState<boolean>(false);
  // Auto-suggested batch/expiry defaults (see effect below) are not a user edit — they
  // must not block ISSUANCE buttons gated on isDirty (6421/6422/6423/6424/6425/transfer),
  // only RECEIPT buttons (7200/7201) which read the saved values. Save button + the
  // unsaved-changes leave-guard treat this the same as isDirty.
  const [hasUnsavedSuggestions, setHasUnsavedSuggestions] = useState<boolean>(false);



  const {
    ingredient,
    clearIngredient,
    loadingIngredient,
    errorList,
    search,
    loadIngredientById,
    usersList,
  } = useRNDetail();

  // RN actuals — Продуктивност is owned by /api/rn-work-orders, keyed by items.
  const {
    rnWorkOrder,
    rnLoading,
    loadRNByItem,
    loadRNById,
    saveProductivity,
    signRN,
    removeApprovedSignature,
    dispatchRazmerStage,
    issueMaterial6421,
    issueMaterial6421Razmer,
    issueMaterial6425Razmer,
    issueRastur6423,
    issueMaterial6422,
    issueSampling6424,
    receiveMaterial7201,
    receiveFinishedProduct7200,
    receiveFinishedProduct7200Prost,
    transferMaterial,
    issueConfiguredWasteDocument,
    transferConfiguredScrapDocument,
    resetPrepared,
    deleteRN,
    clearRN,
    approveMachineRepair,
  } = useRNWorkOrder();
  const { allowedSubprocessIds, canMoveStock, hasNoAssignedStages } = useRNViewerScope({
    viewer: rnWorkOrder?.viewer,
    isAdmin,
    hasWrite,
    activeStage,
    setActiveStage,
  });
  // Preview remains unsaved: it is debounced in the hook and only drives FEFO allocation UI.
  const razmerLotPreviewRows = React.useMemo(() => razmerRows
    .filter((row) => !!row.osRowId)
    .map((row) => ({ rowKey: row.osRowId || '', sourceRowKey: row.sourceRowKey || row.osRowId || '', quantity: row.measuredQty || '', serial: row.serialNumber || '', deadline: row.expiryDate || '' })), [razmerRows]);
  const razmerLotSuggestions = useRNRazmerLots(rnWorkOrder?._id, rnWorkOrder, razmerLotPreviewRows);

  // Одобрил PIN-sign modal (Подготвил has no PIN — it's a server timestamp, see
  // DetailDataSignatureRows mode="rn"). Mirrors /os's signField/ModalSignPin usage.
  const [signField, setSignField] = useState<"approved" | null>(null);

  // Toolbar SEARCH modal lists existing РН work orders (/api/rn-work-orders), NOT the
  // OS normativ. Creating a new work order still happens via the header Идент/Назив
  // selector (OSDetailHeader), which is unaffected by this hook.
  const {
    rnList,
    loading: rnListLoading,
    page: rnPage,
    totalPages: rnTotalPages,
    total: rnTotal,
    absoluteTotal: rnAbsoluteTotal,
    setPage: setRnPage,
    search: rnSearch,
    setSearch: setRnSearch,
    reloadRnList,
    fetchLatestRnRow,
    fetchRnRowById,
  } = useRNWorkOrderList(rnDocumentCode);

  const [confirmDeleteModal, setConfirmDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // RN work order picked in the toolbar search while the form was dirty — held here until
  // the user answers the guard modal. Mirrors OS.tsx's pendingIngredientRef pattern.
  const pendingRnRowRef = React.useRef<RNWorkOrderRow | null>(null);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const [isCreating, setIsCreating] = useState(false);
  const [isInitialHydrating, setIsInitialHydrating] = useState(true);
  // Two kinds of save: a blocking one (toolbar Save, ✓ confirm, timing commit) that swaps
  // the screen for the full-page loader, and the quiet Размерување cell autosave
  // (useRNRazmerAutosave) that must NOT — the operator is already typing in the next
  // cell. `isSaving` (both) still disables every button; only the loader is blocking-only.
  const [isBlockingSave, setIsBlockingSave] = useState(false);
  const [isAutosaving, setIsAutosaving] = useState(false);
  const isSaving = isBlockingSave || isAutosaving;
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showTotalProductivity, setShowTotalProductivity] = useState(false);
  const [showStatusLog, setShowStatusLog] = useState(false);
  const [showMachineRepairs, setShowMachineRepairs] = useState(true);
  const [pendingDocument, setPendingDocument] = useState<WorkOrderRole | 'transfer' | null>(null);
  const [selectedItemCode, setSelectedItemCode] = useState<string>("");
  const [selectedItemName, setSelectedItemName] = useState<string>("");
  const [finishedDeadlineMonths, setFinishedDeadlineMonths] = useState<number | null>(null);

  // Form fields
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  // A specific RN row picked via the toolbar search / navigation (one product has MANY
  // RNs, one per batch). null = no specific RN chosen for the current ident, so the item
  // effect below falls back to "the latest RN of this item" (loadRNByItem), same as
  // before this was tracked. Cleared whenever the ident itself changes by typing.
  const [selectedRnId, setSelectedRnId] = useState<string | null>(null);
  // Keep the by-item link without triggering syncIngredientFields again: that effect
  // replaces the tables/header and clears dirty state. Key it by item to reject stale links.
  const osIngredientLinkRef = React.useRef<{ itemId: string; ingredientId: string } | null>(null);
  const rnLoadStartedForItemRef = React.useRef<string | null>(null);
  const [batch, setBatch] = useState<number>(0);
  const [batchUnit, setBatchUnit] = useState<string | null>(null);
  const [batchUnitCode, setBatchUnitCode] = useState<string>("");
  const [waste, setWaste] = useState<number>(0);
  const [plannedWaste, setPlannedWaste] = useState<number>(0);
  const [validFrom, setValidFrom] = useState<string>("");
  const [validTo, setValidTo] = useState<string>("");
  const [versionDisplay, setVersionDisplay] = useState<string>("001");
  const [productionCode, setProductionCode] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  // Master Batch Record process parameter
  const [equipmentId, setEquipmentId] = useState<string>("");

  // Track the last synced ingredient ID to prevent duplicate syncs
  const [lastSyncedIngredientId, setLastSyncedIngredientId] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL ?? '';

  useEffect(() => {
    const workOrderNumber = rnWorkOrder?.number;
    if (!workOrderNumber) return;
    const receipts = rnWorkOrder?.interProductReceipts || [];
    const receivedStageIds = new Set(receipts
      .filter((receipt) => receiptEntryRole(receipt, documentTypes) === 'receive-intermediate')
      .map((receipt) => receipt.subprocessId));
    const finishedProductLocked = receipts.some((receipt) => receiptEntryRole(receipt, documentTypes) === 'receive-finished');
    let changed = false;
    const next = subprocessItemRows.map((row) => {
      if (row.rowId?.startsWith('finished:')) {
        if (finishedProductLocked) return row;
        const patch: Partial<SubprocessItemRow> = {};
        if (!row.serialNumber) {
          const serialNumber = suggestFinishedBatch(workOrderNumber);
          if (serialNumber) patch.serialNumber = serialNumber;
        }
        if (!row.expiryDate) {
          const expiryDate = suggestFinishedExpiry(rnWorkOrder?.productionDate, finishedDeadlineMonths);
          if (expiryDate) patch.expiryDate = expiryDate;
        }
        if (Object.keys(patch).length === 0) return row;
        changed = true;
        return { ...row, ...patch };
      }
      if (receivedStageIds.has(row.subprocessId)) return row;
      const patch: Partial<SubprocessItemRow> = {};
      if (!row.serialNumber) {
        const serialNumber = suggestInterProductBatch(row.ident, workOrderNumber);
        if (serialNumber) patch.serialNumber = serialNumber;
      }
      if (!row.expiryDate) {
        const expiryDate = suggestInterProductExpiry(rnWorkOrder?.productionDate);
        if (expiryDate) patch.expiryDate = expiryDate;
      }
      if (Object.keys(patch).length === 0) return row;
      changed = true;
      return { ...row, ...patch };
    });
    if (changed) {
      setSubprocessItemRows(next);
      setInterProductActuals(toInterProductActualRows(next));
      // Batch/expiry suggestions are RN-owned actual values: the 7200/7201 routes
      // read them from the saved interProductActuals, not from this component's
      // local state. Require the normal Save before a receipt can use the defaults —
      // but NOT setIsDirty: issuance buttons must stay enabled for this alone.
      setHasUnsavedSuggestions(true);
    }
  }, [rnWorkOrder?.number, rnWorkOrder?.productionDate, rnWorkOrder?.interProductReceipts, finishedDeadlineMonths, subprocessItemRows, setInterProductActuals, toInterProductActualRows, documentTypes]);

  // Merges the "прием" table's edited slice (one stage) back into the full flat
  // subprocessItemRows array — mirrors mergeMaterialnoStageGroups, but by rowId since
  // interProducts rows are already flat (no group nesting to rebuild).
  const mergeInterProductStageRows = React.useCallback((prev: SubprocessItemRow[], rows: SubprocessItemRow[]): SubprocessItemRow[] => {
    const byRowId = new Map(rows.filter((r) => r.rowId).map((r) => [r.rowId, r]));
    return prev.map((r) => (r.rowId && byRowId.has(r.rowId) ? { ...r, ...byRowId.get(r.rowId) } : r));
  }, []);

  const getRazmerSaveDisabledReason = React.useCallback((rows: RazmerRow[]) => (
    rows.some((row) => (
      !!(row.measuredQty || row.serialNumber || row.expiryDate || row.waste || row.rastur)
      && !row.confirmed
      && !row.isPreviewActual
    ))
      ? "Штиклирај ✓ кај размерениот ред пред зачувување"
      : undefined
  ), []);

  const getMaterialnoSaveDisabledReason = React.useCallback((groups: MatGroup[]) => (
    undefined
  ), []);

  const razmerSaveDisabledReason = getRazmerSaveDisabledReason(razmerRows);
  const materialnoSaveDisabledReason = getMaterialnoSaveDisabledReason(materialnoGroups);
  const rnSaveDisabledReason = razmerSaveDisabledReason || materialnoSaveDisabledReason;
  const canSaveRN = (isDirty || hasUnsavedSuggestions) && !rnSaveDisabledReason;

  useEffect(() => {
    if (statusFields.length === 0) {
      setSelectedStatus(null);
      return;
    }
    if (!rnWorkOrder || !rnWorkOrder.status) {
      setSelectedStatus(statusFields[0]._id);
      return;
    }
    setSelectedStatus(rnWorkOrder.status);
  }, [rnWorkOrder, statusFields]);

  // Sync ingredient to form fields
  const syncIngredientFields = React.useCallback((ing: Ingredient) => {
    receiptSourceRef.current = ing;
    // Preload item into cache FIRST to ensure names are available immediately
    if (typeof ing.items === 'object' && ing.items && ing.items._id && ing.items.ident && ing.items.name) {
      // Cast to Item type since we've verified required fields exist
      addItemsToCache([ing.items as Item]);
    }

    const itemId = typeof ing.items === 'string' ? ing.items : ing.items?._id || '';
    setSelectedItemId(itemId);

    // Set item code for display
    if (typeof ing.items === 'object' && ing.items) {
      setSelectedItemCode(ing.items.ident || ing.items.name || '');
    } else if (itemId && getItem) {
      const cachedItem = getItem(itemId);
      if (cachedItem) {
        setSelectedItemCode(cachedItem.ident || cachedItem.name || '');
      }
    }

    setBatch(ing.batch || 0);
    setBatchUnit(typeof (ing as any).batchUnit === 'object' ? (ing as any).batchUnit?._id || null : (ing as any).batchUnit || null);
    setBatchUnitCode(typeof (ing as any).batchUnit === 'object' ? ((ing as any).batchUnit?.code || '') : '');
    setWaste(ing.waste || 0);
    setPlannedWaste(ing.plannedWaste || 0);
    setValidFrom(ing.validFrom || '');
    setValidTo(ing.validTo || '');

    setEquipmentId(ing.equipmentId || '');
    setSelectedSubprocessIds(
      Array.isArray(ing.subprocesses)
        ? ing.subprocesses.map((s: any) => (typeof s === 'object' && s ? s._id : s)).filter(Boolean)
        : []
    );
    setOsProductivity(mapProductivityRows((ing as any).productivity));
    const mappedRazmer = mapRazmerRows((ing as any).razmer);
    setRazmerRows(mergeRazmerActuals(mappedRazmer, razmerActualRowsRef.current));
    const mappedInterProducts = mapRNReceiptRows(ing, isSimpleVariant ? [] : subprocessesRef.current);
    if (mappedInterProducts.length === 0) mappedInterProducts.push(emptySubprocessItemRow());
    setSubprocessItemRows(mergeInterProductActuals(mappedInterProducts, interProductActualRowsRef.current));
    setMaterialnoGroups(mergeMaterialnoActuals(mapMaterialnoGroups((ing as any).materialno, mappedRazmer), materialnoActualRowsRef.current));
    // Нов запис = нова основа за diff-от на un-seeding effect-от.
    prevSeedKeysRef.current = null;
    setInProcessValues({ ...mapParamValueMap((ing as any).inProcessParams), ...rnParamValuesRef.current.inProcess });
    setMontageValues({ ...mapParamValueMap((ing as any).montageParamValues), ...rnParamValuesRef.current.montage });
    const rawVersion = ing.versionDisplay || String(ing.version || 1).padStart(3, '0');
    setVersionDisplay(rawVersion.startsWith('v') ? rawVersion : `v${rawVersion}`);
    setProductionCode((ing as any).productionCode || "");
    setIsCreating(false);
    setLastSyncedIngredientId(ing._id || (ing as any).ingredientId || null);
    setIsDirty(false);
    setHasUnsavedSuggestions(false);
  }, [getItem, addItemsToCache, mergeRazmerActuals, mergeMaterialnoActuals, mergeInterProductActuals, isSimpleVariant]);

  // Sync loaded ingredient to local form state
  useEffect(() => {
    if (ingredient && ingredient._id !== lastSyncedIngredientId) {
      syncIngredientFields(ingredient);
    }
  }, [ingredient, lastSyncedIngredientId, syncIngredientFields]);

  useEffect(() => {
    const source = receiptSourceRef.current;
    const hasIntermediateReceiptStage = Array.isArray(source?.interProducts)
      && source.interProducts.some((interProduct: any) => !!(typeof interProduct?.issueSubprocess === "object"
        ? interProduct.issueSubprocess?._id
        : interProduct?.issueSubprocess));
    if (isSimpleVariant || !source || hasIntermediateReceiptStage || subprocesses.length === 0) return;
    const mappedInterProducts = mapRNReceiptRows(source, subprocesses);
    if (mappedInterProducts.length === 0) return;
    setSubprocessItemRows(mergeInterProductActuals(mappedInterProducts, interProductActualRowsRef.current));
  }, [isSimpleVariant, subprocesses, mergeInterProductActuals, interProductActualRowsRef]);

  // Update item code when selectedItemId changes
  useEffect(() => {
    if (!selectedItemId) {
      setSelectedItemCode('');
      setSelectedItemName('');
      setFinishedDeadlineMonths(null);
      return;
    }
    setFinishedDeadlineMonths(null);
    let cancelled = false;
    (async () => {
      const item = await fetchItemById(selectedItemId);
      if (cancelled || !item) return;
      setSelectedItemCode(item.ident || item.name || '');
      setSelectedItemName(item.name || '');
      setFinishedDeadlineMonths(item.deadlineMonths ?? null);
    })();
    return () => { cancelled = true; };
  }, [selectedItemId, fetchItemById]);

  // RN owns Продуктивност — (re)load it from /api/rn-work-orders whenever the selected
  // ident or the selected RN changes. ONE RN = ONE BATCH: a product has MANY RNs over
  // time, so this is keyed by (item, RN, isCreating), not just the item.
  // - isCreating: a brand-new РН is being built for this ident — do NOT pull in any
  //   existing RN of the same ident (that was the bug: Ново + typed ident used to load
  //   the OLD latest RN and Save would have PUT into it).
  // - selectedRnId set (toolbar search / navigation picked a specific row): load that
  //   exact RN by id, not just "the latest for this item".
  // - neither: ident was typed directly — fall back to the latest RN of this item, as
  //   before.
  useEffect(() => {
    let cancelled = false;
    if (!selectedItemId) {
      rnLoadStartedForItemRef.current = null;
      clearRN();
      clearMachineRepairs();
      setSelectedStatus(null);
      setProductivityRows([emptyProdRow()]);
      setRazmerStartedStatus(null);
      setRazmerActuals([]);
      setRnParamValues({}, {});
      return;
    }
    // React.StrictMode replays effects in development. Keep the same
    // item+RN+creating combination from starting a second identical request during
    // that replay, while still reloading when switching between two RNs of the SAME
    // ident (the key includes selectedRnId/isCreating, not just selectedItemId).
    const loadKey = `${selectedItemId}:${isCreating ? 'new' : (selectedRnId || 'latest')}`;
    if (rnLoadStartedForItemRef.current === loadKey) return;
    rnLoadStartedForItemRef.current = loadKey;
    clearMachineRepairs();
    (async () => {
      let rn: RNWorkOrderDoc | null = null;
      if (isCreating) {
        clearRN();
      } else if (selectedRnId) {
        rn = await loadRNById(selectedRnId);
      } else {
        rn = await loadRNByItem(selectedItemId, rnDocumentCode);
      }
      if (cancelled) return;
      setProductivityRows(
        rn && Array.isArray(rn.productivity) && rn.productivity.length > 0
          ? rn.productivity
          : [emptyProdRow()]
      );
      setSelectedStatus(rn?.status ?? null);
      const nextRazmerActuals = rn && Array.isArray(rn.razmerActuals) ? rn.razmerActuals : [];
      const nextMaterialnoActuals = rn && Array.isArray(rn.materialnoActuals) ? rn.materialnoActuals : [];
      setRazmerActuals(nextRazmerActuals);
      setMaterialnoActuals(nextMaterialnoActuals);
      const nextInterProductActuals = rn && Array.isArray(rn.interProductActuals) ? rn.interProductActuals : [];
      setInterProductActuals(nextInterProductActuals);
      setRazmerRows((prev) => mergeRazmerActuals(prev, nextRazmerActuals));
      setMaterialnoGroups((prev) => mergeMaterialnoActuals(prev, nextMaterialnoActuals));
      setSubprocessItemRows((prev) => mergeInterProductActuals(prev, nextInterProductActuals));
      setRnParamValues(mapParamValueMap(rn?.inProcessParams), mapParamValueMap(rn?.montageParamValues));
      setMachineRepairs(rn?.machineRepairs || []);
    })();
    return () => { cancelled = true; };
  }, [selectedItemId, isCreating, selectedRnId, loadRNByItem, loadRNById, clearRN, clearMachineRepairs, mergeRazmerActuals, mergeMaterialnoActuals, mergeInterProductActuals, setRazmerActuals, setMaterialnoActuals, setInterProductActuals, setRnParamValues, setMachineRepairs]);

  useEffect(() => {
    setRazmerStartedStatus(null);
  }, [selectedItemId, rnWorkOrder?._id]);

  // Copy the OS master-recipe subprocesses (read-only) for the selected ident.
  // RN never edits these. Populates selectedSubprocessIds so the left stage tree
  // reflects the majka's subprocess selection.
  useEffect(() => {
    let cancelled = false;
    osIngredientLinkRef.current = null;
    receiptSourceRef.current = null;
    if (!selectedItemId) {
      setSelectedSubprocessIds([]);
      setOsProductivity([]);
      setRazmerRows([emptyRazmerRow()]);
      setSubprocessItemRows([emptySubprocessItemRow()]);
      setMaterialnoGroups([emptyGroup(0, "1")]);
      prevSeedKeysRef.current = null;
      return;
    }
    const snapshot = rnWorkOrder?.normativSnapshot;
    if (snapshot) {
      // New RNs are rendered from the hydrated response copy of their immutable
      // snapshot. Do not replace it with the current OS normativ by item.
      syncIngredientFields({
        ...snapshot,
        _id: snapshot.ingredientId || snapshot._id,
        version: rnWorkOrder.normativVersion ?? snapshot.version,
      } as Ingredient);
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${apiUrl}/api/ingredients/by-item/${selectedItemId}`, {
          headers: { 'x-auth-token': token },
        });
        if (cancelled) return;
        const osIngredient = res.data?.ingredient;
        receiptSourceRef.current = osIngredient;
        if (osIngredient?._id) {
          osIngredientLinkRef.current = { itemId: selectedItemId, ingredientId: osIngredient._id };
        }
        const subs = Array.isArray(res.data?.ingredient?.subprocesses) ? res.data.ingredient.subprocesses : [];
        const mappedRazmer = mapRazmerRows(osIngredient?.razmer);
        const mappedInterProducts = mapRNReceiptRows(osIngredient, isSimpleVariant ? [] : subprocessesRef.current);
        setSelectedSubprocessIds(subs.map((s: any) => (typeof s === 'object' && s ? s._id : s)).filter(Boolean));
        setOsProductivity(mapProductivityRows(osIngredient?.productivity));
        setRazmerRows(mappedRazmer.length > 0 ? mergeRazmerActuals(mappedRazmer, razmerActualRowsRef.current) : [emptyRazmerRow()]);
        setSubprocessItemRows(mappedInterProducts.length > 0 ? mergeInterProductActuals(mappedInterProducts, interProductActualRowsRef.current) : [emptySubprocessItemRow()]);
        // Материјално мора да дојде ОД ТУКА, не само од syncIngredientFields: РН документ со
        // `ingredient: null` (создаден преку Идент селекторот, без врзан норматив) никогаш не
        // поминува низ syncIngredientFields, па табелата остануваше празна и се пишуваше рачно.
        setMaterialnoGroups(mergeMaterialnoActuals(mapMaterialnoGroups(osIngredient?.materialno, mappedRazmer), materialnoActualRowsRef.current));
        prevSeedKeysRef.current = null;
      } catch {
        if (!cancelled) {
          setSelectedSubprocessIds([]);
          setOsProductivity([]);
          setRazmerRows([emptyRazmerRow()]);
          setSubprocessItemRows([emptySubprocessItemRow()]);
          setMaterialnoGroups([emptyGroup(0, "1")]);
          prevSeedKeysRef.current = null;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [apiUrl, selectedItemId, rnWorkOrder?.normativSnapshot, syncIngredientFields, mergeRazmerActuals, mergeMaterialnoActuals, mergeInterProductActuals, setRazmerActuals, isSimpleVariant]);


  const resetForm = () => {
    osIngredientLinkRef.current = null;
    receiptSourceRef.current = null;
    clearIngredient();
    setLastSyncedIngredientId(null);
    setRazmerActuals([]);
    setMaterialnoActuals([]);
    setSelectedItemId("");
    setSelectedRnId(null);
    setSelectedItemCode("");
    setSelectedItemName("");
    setBatch(0);
    setBatchUnit(null);
    setBatchUnitCode("");
    setWaste(0);
    setPlannedWaste(0);
    setValidFrom("");
    setValidTo("");
    setEquipmentId("");
    setSelectedSubprocessIds([]);
    setOsProductivity([]);
    setProductivityRows([emptyProdRow()]);
    setRazmerStartedStatus(null);
    setRazmerRows([emptyRazmerRow()]);
    setMaterialnoGroups([emptyGroup(0, "1")]);
    setSubprocessItemRows([emptySubprocessItemRow()]);
    setInProcessValues({});
    setMontageValues({});
    clearMachineRepairs();
    setVersionDisplay("001");
    setProductionCode("");
    setSelectedStatus(statusFields[0]?._id ?? null);
    setIsCreating(true);
    setIsDirty(false);
    setHasUnsavedSuggestions(false);
  };


  const handleDeleteRequest = async () => {
    if (!rnWorkOrder?._id) return;
    setConfirmDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteModal(false);
  };

  const handleDeleteConfirm = async () => {
    if (!rnWorkOrder?._id || isDeleting) return;
    setIsDeleting(true);
    setConfirmDeleteModal(false);
    try {
      await deleteRN(rnWorkOrder._id);
      showToast(t("toast.deleted", { item: rnWorkOrder.number || t("tableHeaders.workOrder") }) || "Deleted", "success");
      reloadRnList();
      const latest = await fetchLatestRnRow();
      if (latest) {
        loadRnRow(latest);
      } else {
        autoLoadedLatestRef.current = false;
        resetForm();
      }
    } catch (err) {
      // Error toast already shown by deleteRN; swallow to avoid unhandled rejection.
    } finally {
      setIsDeleting(false);
    }
  };

  // Одобрил — PIN sign. Mirrors OS.tsx's handleSign: catches so a wrong PIN shows a
  // toast, not an unhandled rejection (ModalSignPin keeps signField set for a retry).
  const handleSign = async (pin: string) => {
    if (!rnWorkOrder?._id || !signField) return;
    try {
      await signRN(pin);
      setSignField(null);
      showToast(t("qualDoc.signedSuccessfully") || "Signed successfully", "success");
    } catch (err: any) {
      showToast(err?.response?.data?.message || t("errors.signFailed") || "Signature failed", "error");
    }
  };

  const handleRemoveApproved = async () => {
    if (!rnWorkOrder?._id) return;
    try {
      await removeApprovedSignature();
      showToast(t("common.savedSuccessfully") || "Saved", "success");
    } catch (err) {
      // Error toast already shown by removeApprovedSignature; swallow.
    }
  };

  // Admin-only — clears Подготвил (backend also clears Одобрил with it).
  const handleResetPrepared = async () => {
    if (!rnWorkOrder?._id) return;
    try {
      await resetPrepared();
      showToast(t("common.savedSuccessfully") || "Saved", "success");
    } catch (err) {
      // Error toast already shown by resetPrepared; swallow.
    }
  };

  // Loads an RN work-order row into the screen. Shared by the toolbar search select and
  // the auto-load-latest-on-mount effect below. Picks THIS specific RN, not just the
  // latest one for its ident — row._id is tracked in selectedRnId so the item-loading
  // effect calls loadRNById instead of falling back to "latest".
  const loadRnRow = React.useCallback((row: RNWorkOrderRow) => {
    clearMachineRepairs();
    const itemObj = typeof row.items === 'object' && row.items ? row.items : null;
    if (itemObj && itemObj._id && itemObj.ident && itemObj.name) {
      addItemsToCache([itemObj as Item]);
    }
    setSelectedRnId(row._id);
    // ALWAYS set the ident from the RN row, so the master header (Идент/Назив) and the
    // productivity load populate even when the RN has no (or a stale) linked OS ingredient.
    if (itemObj && itemObj._id) {
      setSelectedItemId(itemObj._id);
    }
    if (row.ingredient) {
      // Also load the OS ingredient reference (subprocesses / razmer / header extras).
      setLastSyncedIngredientId(null);
      const ingredientId = typeof row.ingredient === 'object' ? row.ingredient._id : row.ingredient;
      loadIngredientById(ingredientId);
    }
    setIsCreating(false);
  }, [addItemsToCache, clearMachineRepairs, loadIngredientById]);

  const handleUnsavedConfirm = React.useCallback(() => {
    setShowUnsavedModal(false);
    const pending = pendingRnRowRef.current;
    pendingRnRowRef.current = null;
    setIsDirty(false);
    setHasUnsavedSuggestions(false);
    if (pending) loadRnRow(pending);
  }, [loadRnRow]);

  const handleUnsavedCancel = React.useCallback(() => {
    setShowUnsavedModal(false);
    pendingRnRowRef.current = null;
  }, []);

  // Default the screen to the most recently updated РН work order on first open
  // (mirrors how the reception/1000 screen auto-loads its latest document). Runs
  // once; never overrides an already-loaded ident (e.g. a kept-alive open tab).
  const autoLoadedLatestRef = React.useRef(false);
  const handledNavigationWorkOrderRef = React.useRef<string | null>(null);
  const navigatedWorkOrderId = (location.state as { workOrderId?: string } | null)?.workOrderId;
  useEffect(() => {
    if (!navigatedWorkOrderId) {
      handledNavigationWorkOrderRef.current = null;
      return;
    }
    if (handledNavigationWorkOrderRef.current === navigatedWorkOrderId) return;
    let cancelled = false;
    let settled = false;
    // Claim the id BEFORE the request: this effect re-runs on every render while the RN
    // hydrates (loadRnRow and friends change identity), and each re-run used to fire
    // another GET — 11 identical requests per open, measured 2026-10-11.
    handledNavigationWorkOrderRef.current = navigatedWorkOrderId;
    setIsInitialHydrating(true);
    fetchRnRowById(navigatedWorkOrderId)
      .then((target) => {
        settled = true;
        if (cancelled) return;
        autoLoadedLatestRef.current = true;
        loadRnRow(target);
        const navigationState = location.state as { machineRepairRequest?: unknown } | null;
        navigate(location.pathname, {
          replace: true,
          state: navigationState?.machineRepairRequest ? { machineRepairRequest: navigationState.machineRepairRequest } : null,
        });
      })
      .catch((error) => {
        settled = true;
        if (cancelled) return;
        handledNavigationWorkOrderRef.current = null;
        setIsInitialHydrating(false);
        showToast(error?.response?.data?.message || error?.message || t('common.error'), 'error');
      });
    return () => {
      cancelled = true;
      // Torn down mid-flight (a dependency really changed): release the claim so the
      // re-run fetches again instead of discarding the response and never loading.
      if (!settled) handledNavigationWorkOrderRef.current = null;
    };
  }, [navigatedWorkOrderId, fetchRnRowById, loadRnRow, navigate, location.pathname, showToast, t]);
  useEffect(() => {
    if (navigatedWorkOrderId) return;
    if (autoLoadedLatestRef.current) return;
    if (selectedItemId) return;
    if (rnList.length === 0) return;
    autoLoadedLatestRef.current = true;
    setIsInitialHydrating(true);
    loadRnRow(rnList[0]);
  }, [rnList, selectedItemId, loadRnRow, navigatedWorkOrderId]);

  // Keep one continuous loader across the list -> RN -> ingredient -> form
  // hydration chain. Without this gate each request starts a separate render
  // cycle and the user sees multiple loading flashes.
  useEffect(() => {
    if (!isInitialHydrating || rnListLoading) return;
    if (rnList.length === 0) {
      setIsInitialHydrating(false);
      return;
    }
    if (!autoLoadedLatestRef.current || !selectedItemId) return;
    if (loadingIngredient || rnLoading) return;
    if (ingredient && ingredient._id !== lastSyncedIngredientId) return;
    setIsInitialHydrating(false);
  }, [isInitialHydrating, rnListLoading, rnList.length, selectedItemId, loadingIngredient, rnLoading, ingredient, lastSyncedIngredientId]);

  const handlePrintPdf = () => {
    navigate("/ingridiens-control-panel", {
      state: {
        ingredient: ingredient || null,
        returnPath: `/work-order/${rnDocumentCode}`,
      },
    });
  };

  // Persists RN actuals to /api/rn-work-orders. Never calls
  // updateIngredient/createIngredient — those hit /api/ingredients (OS), which RN
  // must not write to.
  //
  // `quiet` = Размерување cell autosave (useRNRazmerAutosave): no full-page loader, no
  // success toast (the cell shows ✓), no validation toast (the hook already checked the
  // save guards and simply leaves a blocked cell dirty), and the server echo is merged
  // without clobbering whatever the operator typed while the PUT was in flight.
  // Every save — quiet or not — goes through `saveChainRef`, so two PUTs never overlap.
  const performSave = async (rowsOverride: RazmerRow[] | MatGroup[] | { interProductRows: SubprocessItemRow[] } | unknown, quiet: boolean) => {
    const setBusy = quiet ? setIsAutosaving : setIsBlockingSave;
    const razmerRowsForSave = Array.isArray(rowsOverride) && (rowsOverride[0] as any)?.items === undefined
      ? rowsOverride as RazmerRow[]
      : razmerRows;
    const materialnoGroupsForSave = Array.isArray(rowsOverride) && (rowsOverride[0] as any)?.items !== undefined
      ? rowsOverride as MatGroup[]
      : materialnoGroups;
    const subprocessItemRowsForSave = rowsOverride && !Array.isArray(rowsOverride) && typeof rowsOverride === 'object' && 'interProductRows' in rowsOverride
      ? (rowsOverride as { interProductRows: SubprocessItemRow[] }).interProductRows
      : subprocessItemRows;
    if (!selectedItemId) {
      if (!quiet) setShowValidationErrors(true);
      return false;
    }
    const saveDisabledReason = getRazmerSaveDisabledReason(razmerRowsForSave)
      || getMaterialnoSaveDisabledReason(materialnoGroupsForSave);
    if (saveDisabledReason) {
      if (!quiet) showToast(saveDisabledReason, "error");
      return false;
    }

    if (!quiet) setShowValidationErrors(false);

    const sentStageValues = JSON.stringify([
      materialnoGroupsForSave, productivityRows, subprocessItemRowsForSave, inProcessValues, montageValues,
    ]);

    try {
      setBusy(true);
      const rnItemId = typeof rnWorkOrder?.items === 'string' ? rnWorkOrder.items : rnWorkOrder?.items?._id;
      const linkedIngredient = rnItemId === selectedItemId ? rnWorkOrder?.ingredient : null;
      const linkedIngredientId = typeof linkedIngredient === 'string' ? linkedIngredient : linkedIngredient?._id;
      const ingredientItemId = typeof ingredient?.items === 'string' ? ingredient.items : ingredient?.items?._id;
      const osLink = osIngredientLinkRef.current;
      // Preserve the saved RN reference (including archived versions); repair only missing links.
      const ingredientId = linkedIngredientId
        || (ingredientItemId === selectedItemId ? ingredient?._id : null)
        || (osLink?.itemId === selectedItemId ? osLink.ingredientId : null);
      const savedRN = await saveProductivity({
        documentCode: rnDocumentCode,
        status: selectedStatus,
        items: selectedItemId,
        ingredient: ingredientId || null,
        productivity: productivityRows,
        razmerActuals: toRazmerActualRows(razmerRowsForSave),
        materialnoActuals: toMaterialnoActualRows(materialnoGroupsForSave),
        interProductActuals: toInterProductActualRows(subprocessItemRowsForSave),
        inProcessParams: toParamEntries(inProcessValues),
        montageParamValues: toParamEntries(montageValues),
        machineRepairs: toRNMachineRepairsPayload(machineRepairs),
      });
      if (!savedRN?._id) {
        setBusy(false);
        return false;
      }
      if (savedRN?._id && isCreating) {
        // A brand-new РН was just created (POST). Point the screen at this specific
        // RN — not just "the latest for this item", which happens to be it right now
        // but would stop being it the moment another RN is created later — and
        // pre-seed the item-loading effect's guard so it does not immediately
        // re-fetch the document this save already returned.
        rnLoadStartedForItemRef.current = `${selectedItemId}:${savedRN._id}`;
        setSelectedRnId(savedRN._id);
        setIsCreating(false);
        reloadRnList();
      }
      // Quiet save, and a subprocess-table cell changed while the PUT ran: keep the local
      // values (and stay dirty) instead of merging the older server echo over them.
      const stageEditedMeanwhile = quiet && stageValuesRef.current !== sentStageValues;
      const savedRazmerActuals = savedRN && Array.isArray(savedRN.razmerActuals) ? savedRN.razmerActuals : [];
      const savedMaterialnoActuals = savedRN && Array.isArray(savedRN.materialnoActuals) ? savedRN.materialnoActuals : [];
      const savedInterProductActuals = savedRN && Array.isArray(savedRN.interProductActuals) ? savedRN.interProductActuals : [];
      setRazmerActuals(savedRazmerActuals);
      if (!stageEditedMeanwhile) {
        setMaterialnoActuals(savedMaterialnoActuals);
        setInterProductActuals(savedInterProductActuals);
      }
      // Quiet: the operator may have moved on to the next cell while this PUT ran —
      // keep what was typed since the send, and stay dirty only if something was kept.
      const kept = quiet
        ? keepRazmerLocalEdits(razmerRowsRef.current, razmerRowsForSave, mergeRazmerActuals(razmerRowsRef.current, savedRazmerActuals))
        : null;
      if (kept) setRazmerRows(kept.rows);
      else setRazmerRows((prev) => mergeRazmerActuals(prev, savedRazmerActuals));
      if (!stageEditedMeanwhile) {
        setMaterialnoGroups((prev) => mergeMaterialnoActuals(prev, savedMaterialnoActuals));
        setSubprocessItemRows((prev) => mergeInterProductActuals(prev, savedInterProductActuals));
        setRnParamValues(
          mapParamValueMap(savedRN?.inProcessParams),
          mapParamValueMap(savedRN?.montageParamValues)
        );
      }
      setMachineRepairs(savedRN?.machineRepairs || []);
      setSelectedStatus(savedRN?.status ?? statusFields[0]?._id ?? null);
      setBusy(false);
      setIsDirty(!!kept?.keptLocalEdits || stageEditedMeanwhile);
      setHasUnsavedSuggestions(false);
      if (!quiet) showToast(t("common.savedSuccessfully") || "Saved", "success");
      return true;
    } catch (error) {
      setBusy(false);
      return false;
    }
  };

  const handleSaveIngredient = async (
    rowsOverride?: RazmerRow[] | MatGroup[] | { interProductRows: SubprocessItemRow[] } | unknown,
    options?: { quiet?: boolean },
  ): Promise<boolean> => {
    const quiet = options?.quiet === true;
    const run = saveChainRef.current.then(() => performSave(rowsOverride, quiet));
    saveChainRef.current = run.catch(() => undefined);
    return run;
  };

  // Размерување cell autosave (Кол. раз./Отпад/Растур) — owner decision 2026-10-09. Only on
  // an already created RN the user may write to; the hook re-checks the save guards
  // (✓ rule) before every PUT and otherwise leaves the cell dirty for the manual Save.
  const razmerAutosave = useRNRazmerAutosave({
    enabled: !!rnWorkOrder?._id && !isCreating && hasWrite("WORK_ORDERS"),
    rows: razmerRows,
    isClean: !isDirty,
    canSave: (rows) => !!selectedItemId && !getRazmerSaveDisabledReason(rows),
    save: (rows) => handleSaveIngredient(rows, { quiet: true }),
  });

  // Subprocess tables (everything below the stage tree except Размерување): save quietly when
  // the operator leaves an edited cell — no trip back up to the toolbar Save.
  const stageAutosave = useStageAutosave({
    trigger: "cell",
    enabled: !!rnWorkOrder?._id && !isCreating && hasWrite("WORK_ORDERS") && activeStage !== RAZMER_STAGE_ID,
    isDirty,
    save: () => handleSaveIngredient(undefined, { quiet: true }),
  });

  handleSaveRef.current = async () => { await handleSaveIngredient(); };

  // ✓ (and the other one-click Размерување actions) save at once — quietly since 2026-10-09:
  // no full-page loader, a spinner/✓ in the row's Кол. раз. cell instead. A blocked save still
  // explains itself with the usual toast; server errors come from saveProductivity.
  const [confirmSaveStatus, setConfirmSaveStatus] = useState<RazmerCellSaveStatusMap>({});
  const handleRazmerChangeAndSave = React.useCallback((rows: RazmerRow[]) => {
    const previous = razmerRowsRef.current;
    const changedKeys = rows
      .filter((row, index) => {
        const before = previous[index];
        return !before
          || before.osRowId !== row.osRowId
          || !!before.confirmed !== !!row.confirmed
          || before.measuredQty !== row.measuredQty
          || before.serialNumber !== row.serialNumber;
      })
      .map((row) => razmerAutosaveCellKey(row, "measuredQty"));
    setRazmerRows(rows);
    setRazmerActuals(toRazmerActualRows(rows));
    setIsDirty(true);
    const blockedReason = getRazmerSaveDisabledReason(rows);
    if (blockedReason) {
      showToast(blockedReason, "error");
      return;
    }
    const mark = (status?: "saving" | "saved") => setConfirmSaveStatus((prev) => {
      const next = { ...prev };
      changedKeys.forEach((key) => {
        if (status) next[key] = status;
        else delete next[key];
      });
      return next;
    });
    mark("saving");
    void handleSaveIngredient(rows, { quiet: true }).then((ok) => {
      mark(ok ? "saved" : undefined);
      if (ok) window.setTimeout(() => mark(undefined), 2000);
    });
  }, [handleSaveIngredient, setRazmerActuals, toRazmerActualRows, showToast, getRazmerSaveDisabledReason]);
  const razmerCellSaveStatus = React.useMemo(
    () => ({ ...razmerAutosave.cellSaveStatus, ...confirmSaveStatus }),
    [razmerAutosave.cellSaveStatus, confirmSaveStatus],
  );

  const mergeMaterialnoStageGroups = React.useCallback((prev: MatGroup[], groups: MatGroup[]): MatGroup[] => {
    if (!activeStage) return prev;
    return [
      ...prev.filter((grp) => grp.subprocessId !== activeStage),
      ...groups.map((grp) => ({ ...grp, subprocessId: activeStage })),
    ];
  }, [activeStage]);

  const handleMaterialnoChangeAndSave = React.useCallback((groups: MatGroup[]) => {
    const nextGroups = mergeMaterialnoStageGroups(materialnoGroups, groups);
    setMaterialnoGroups(nextGroups);
    setMaterialnoActuals(toMaterialnoActualRows(nextGroups));
    setIsDirty(true);
    void handleSaveIngredient(nextGroups, { quiet: true });
  }, [handleSaveIngredient, materialnoGroups, mergeMaterialnoStageGroups, setMaterialnoActuals, toMaterialnoActualRows]);

  const handleInterProductChangeAndSave = (rows: SubprocessItemRow[]) => {
    const next = mergeInterProductStageRows(subprocessItemRows, rows);
    setSubprocessItemRows(next);
    setInterProductActuals(toInterProductActualRows(next));
    setIsDirty(true);
    void handleSaveIngredient({ interProductRows: next }, { quiet: true });
  };

  const handleRazmerPreviewChange = React.useCallback((rows: RazmerRow[]) => {
    setRazmerRows(rows);
  }, []);

  const {
    showExportModal,
    setShowExportModal,
    isExporting,
    handleExportAll,
    handleExportCurrent,
    handleExportFiltered,
  } = useRNIngredientExcelExport({
    ingredient,
    selectedItemCode,
    language: i18n.language,
    showToast,
    t,
  });

  // Which group does the selected stage belong to? Размерування group → weighing
  // table; Припрема/Пакување → Материјално. (Temporary: match by processDefinition
  // name; the stable isWeighing flag on ProcessDefinition is planned.)
  const isRazmerStage = activeStage === RAZMER_STAGE_ID;
  // Timing Enter commits the productivity row first, then persists the updated RN so
  // the /work-orders register (including other users' sessions) sees the reached status.
  const saveAfterTimingCommit = React.useCallback(() => {
    setIsBlockingSave(true);
    window.setTimeout(() => { void handleSaveRef.current(); }, 0);
  }, []);
  const razmerStatusLabel =
    razmerStartedStatus
    && selectedItemId
    && rnWorkOrder?._id
    && razmerStartedStatus.itemId === selectedItemId
    && razmerStartedStatus.workOrderId === rnWorkOrder._id
      ? productionStatusOptions.find((option) => option._id === razmerStartedStatus.statusId)?.name
      : undefined;
  const activeSubprocess = subprocesses.find((sp) => sp._id === activeStage);
  const activeConfiguredStatuses = activeSubprocess?.statuses || [];
  const stageStatusLabel = razmerStatusLabel || (razmerStartedStatus && razmerStartedStatus.stageId === activeStage
    ? (activeConfiguredStatuses[razmerStartedStatus.statusIndex] as any)?.name
    : undefined);
  // Screen opens on Размерување. Keep „Машини и поправки“ visible by falling back to
  // the first ident subprocess that has machines assigned in the codebook.
  const machineTableSubprocess = React.useMemo(() => {
    if (activeSubprocess) return activeSubprocess;
    const selected = subprocesses.filter((sp) => selectedSubprocessIds.includes(sp._id));
    return selected.find((sp) => (sp.machines || []).length > 0) || selected[0];
  }, [activeSubprocess, subprocesses, selectedSubprocessIds]);
  const { canApproveMachineRepairs, approvingKey, handleApproveMachineRepair } = useRNMachineRepairApproval({
    rnWorkOrder,
    user,
    approveMachineRepair,
    setMachineRepairs,
    showToast,
    t,
  });
  const {
    machineOptions,
    activeStageMachineIds,
    visibleProdRows,
    totalProductivityRows,
  } = useRNProductivityPlan({
    isRazmerStage,
    activeStage,
    activeSubprocess,
    subprocesses,
    osProductivity,
    productivityRows,
    selectedSubprocessIds,
    machineSeeds: rnWorkOrder?.normativSnapshot?.machineSeeds,
  });

  const processGroupIdBySubprocess = React.useMemo(() => {
    const map = new Map<string, string>();
    subprocesses.forEach((sp) => {
      const pd = sp.processDefinition;
      if (pd && typeof pd === "object" && pd._id) map.set(sp._id, pd._id);
    });
    return map;
  }, [subprocesses]);
  const processGroupOrder = React.useMemo(() => buildProcessGroupOrder(subprocesses), [subprocesses]);

  const {
    dispatchModalStage,
    setDispatchModalStage,
    dispatchUserId,
    setDispatchUserId,
    isDispatching,
    razmerDispatchGroupIds,
    razmerDispatchLabels,
    buildDispatchRows,
    getDispatchState,
    openDispatchModal,
    handleDispatchSubmit,
  } = useRNRazmerDispatch({
    razmerRows,
    processGroupIdBySubprocess,
    processGroupOrder,
    dispatchRazmerStage,
    showToast,
  });

  // Продуктивност / Размерување / Материјално are per-subprocess: the flat state arrays
  // hold rows for ALL stages (that's what gets saved); each table only ever sees the
  // slice belonging to activeStage. Legacy rows with no subprocess (null/'') never
  // appear in any slice — they are demo data, not migrated.
  // Rows are DRIVEN BY the OS master recipe: one row per machine OS planned for the
  // active subprocess. Машини / planned Времетраење (x3) / Број на оператори come from
  // osProductivity (read-only); Почетно/Крајно (actuals) are merged in from the RN-owned
  // productivityRows, matched by machine + subprocess.
  // ── Материјално се ИЗВЕДУВА, не се куца ─────────────────────────────────────────────────
  // Ист договор како на /os: што е размерено за фазата, и што ѝ се ИЗДАВА како меѓупроизвод,
  // е нејзин материјал. На РН тоа важи со уште поголема сила — handleSaveIngredient праќа само
  // productivity + razmerActuals, па сè што се куцаше тука се губеше при зачувување.
  //
  // Разлика од /os: тука „Подпроцес издавање" е ВЕЌЕ снимен на редот од серверот
  // (normalizeInterProducts), па снимената вредност има предност; деривацијата е фаллбек за
  // затекнати редови снимени пред тоа поле да постои.
  const interProductIssueSubprocessIds = React.useMemo(() => {
    const derived = deriveIssueSubprocessIds(subprocessItemRows, subprocesses, selectedSubprocessIds);
    return subprocessItemRows.map((r, i) => r.rowId?.startsWith('finished:') ? '' : (r.issueSubprocessId || derived[i] || ''));
  }, [subprocessItemRows, subprocesses, selectedSubprocessIds]);

  // Latest subprocess-table values: a quiet save must not overwrite what was typed while its PUT ran.
  const stageValuesRef = React.useRef("");
  stageValuesRef.current = JSON.stringify([materialnoGroups, productivityRows, subprocessItemRows, inProcessValues, montageValues]);

  useRNMaterialnoSeeding({
    activeStage,
    isRazmerStage,
    isCreating,
    rnWorkOrder,
    activeSubprocess,
    razmerRows,
    subprocessItemRows,
    interProductIssueSubprocessIds,
    documentTypes,
    materialnoGroups,
    materialnoActualRowsRef,
    mergeMaterialnoActuals,
    setMaterialnoGroups,
    setIsDirty,
    showToast,
    t,
  });
  // Другата половина: изворниот ред исчезна → исчезнува и Материјално редот. Diff спрема
  // претходниот render, не „избриши сè без извор", за да не се збрише затекнат/увезен ред што
  // никогаш немал извор.
  useEffect(() => {
    const keyOf = (subprocessId: string, itemId: string) => `${subprocessId}|${itemId}`;
    const current = new Set<string>();
    razmerRows.forEach((r) => { if (r.itemId) current.add(keyOf(r.subprocessId, r.itemId)); });
    subprocessItemRows.forEach((r, i) => {
      const issueSubprocessId = interProductIssueSubprocessIds[i];
      if (r.itemId && issueSubprocessId) current.add(keyOf(issueSubprocessId, r.itemId));
    });

    const prev = prevSeedKeysRef.current;
    prevSeedKeysRef.current = current;
    if (!prev) return;

    const removed = new Set<string>();
    prev.forEach((k) => { if (!current.has(k)) removed.add(k); });
    if (removed.size === 0) return;

    setMaterialnoGroups((groups) => {
      let changed = false;
      const next = groups.map((g) => {
        const kept = g.items.filter((it) => !it.itemId || !removed.has(keyOf(g.subprocessId, it.itemId)));
        if (kept.length === g.items.length) return g;
        changed = true;
        return { ...g, items: kept };
      });
      return changed ? next : groups;
    });
  }, [razmerRows, subprocessItemRows, interProductIssueSubprocessIds]);

  const visibleRazmerRows = React.useMemo(() => {
    if (!isRazmerStage) return [];
    return razmerRows.length > 0 ? razmerRows : [emptyRazmerRow()];
  }, [razmerRows, isRazmerStage]);

  const visibleMaterialnoGroups = React.useMemo(() => {
    if (!activeStage) return [];
    const groups = materialnoGroups.filter((g) => g.subprocessId === activeStage);
    // Реден бр. живее во Размерување — се пресликува живо, како на /os, за да не се разиде
    // од редот што го прикажува табелата погоре.
    const priorityByItem = new Map(
      razmerRows
        .filter((r) => r.subprocessId === activeStage && r.itemId)
        .map((r) => [r.itemId, r.priority])
    );
    const withRazmerPriority = groups.map((g) => ({
      ...g,
      items: g.items.map((it) => ({ ...it, priority: priorityByItem.get(it.itemId) || it.priority || '' })),
    }));
    return withRazmerPriority.length > 0 ? withRazmerPriority : [{ ...emptyGroup(0, "1"), subprocessId: activeStage }];
  }, [materialnoGroups, activeStage, razmerRows]);

  const preparationDispatchState = getDispatchState("preparation");
  const packagingDispatchState = getDispatchState("packaging");
  const dispatchModalRows = dispatchModalStage ? buildDispatchRows(dispatchModalStage) : [];
  const dispatchSenderName = user
    ? [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.userName
    : "";

  // Материјално Копирај: само по испратено Размерување за групата на активната фаза,
  // и само вредности од Количина размерена (measuredQty).
  const materialnoAllowCopyFromRazmer = React.useMemo(() => {
    if (!activeStage || isRazmerStage) return false;
    const groupId = processGroupIdBySubprocess.get(activeStage) || "";
    if (!groupId) return false;
    const dispatches = rnWorkOrder?.razmerDispatches || [];
    if (groupId === razmerDispatchGroupIds.preparation) {
      return dispatches.some((d) => d.stage === "preparation");
    }
    if (groupId === razmerDispatchGroupIds.packaging) {
      return dispatches.some((d) => d.stage === "packaging");
    }
    return false;
  }, [
    activeStage,
    isRazmerStage,
    processGroupIdBySubprocess,
    razmerDispatchGroupIds.packaging,
    razmerDispatchGroupIds.preparation,
    rnWorkOrder?.razmerDispatches,
  ]);

  const materialnoRazmerCopySourceByItemId = React.useMemo(() => {
    if (!activeStage || isRazmerStage) return {};
    return buildRazmerCopySources(razmerRows, activeStage);
  }, [activeStage, isRazmerStage, razmerRows]);

  const {
    isIssuingMaterial6421,
    isReceivingMaterial7201,
    isIssuingRastur6423,
    rasturModalOpen,
    setRasturModalOpen,
    subprocessNameById,
    materialno6421Disabled,
    materialno6421Title,
    handleIssueMaterial6421,
    rastur6423StageOptions,
    rastur6423Disabled,
    rastur6423Title,
    handleSelectRasturStage,
    visibleInterProductRows,
    interProduct7201Disabled,
    interProduct7201Title,
    handleReceiveMaterial7201,
    documentBusy,
  } = useRNMaterialDocumentActions({
    rnWorkOrder,
    rnDocumentCode,
    activeStage,
    isRazmerStage,
    isSaving,
    isDirty,
    hasUnsavedSuggestions,
    pendingDocument,
    materialnoGroups,
    razmerRows,
    subprocesses,
    subprocessItemRows,
    documentTypes,
    issueMaterial6421,
    issueRastur6423,
    receiveMaterial7201,
    showToast,
    t,
  });

  const {
    pendingConfiguredDocument,
    configuredPickerOpen,
    configuredPickerTitle,
    setConfiguredPickerOpen,
    configuredStageOptions,
    selectConfiguredStage,
    getConfiguredAction,
  } = useRNConfiguredDocumentActions({
    rnWorkOrder,
    activeStage,
    isSaving,
    isDirty,
    pendingDocument,
    razmerRows,
    materialnoGroups,
    subprocesses,
    documentTypes,
    issueConfiguredWasteDocument,
    transferConfiguredScrapDocument,
    showToast,
    t,
  });

  // The server resolves and freezes this configuration when the work order is created.
  // Deduplication is deliberately by material-document code: multiple creation rules can
  // point to one document, but must never create duplicate RN actions.
  const configuredDocumentButtons = React.useMemo(() => {
    const seen = new Set<string>();
    return (rnWorkOrder?.buttonConfig || []).filter((config) => {
      const code = currentDocTypeCode(config, documentTypes);
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
  }, [rnWorkOrder?.buttonConfig, documentTypes]);

  const getConfiguredDocumentButtonLabel = (config: NonNullable<RNWorkOrderDoc["buttonConfig"]>[number]) => {
    const configuredLabel = (i18n.language || "").startsWith("en") ? config.labelEnglish : config.label;
    if (configuredLabel?.trim()) return configuredLabel.trim();

    const fallbackKeys: Record<WorkOrderRole, string> = {
      "issue-materials": "os.materialno.issue6421",
      "issue-intermediate": "os.materialno.issue6422",
      "issue-finished": "os.materialno.issue6425",
      "receive-finished": "os.materialno.receive7200",
      "waste": "os.razmer.rastur6423",
      "sampling": "rnUzurkuvanjeRequests.openButton",
      "sample-transfer": "rnUzurkuvanjeRequests.sampledButton",
      "receive-intermediate": "os.materialno.receive7201",
      "scrap": "documentTypeConfig.createTransfer",
      "qp-release-transfer": "documentTypeConfig.workOrderRoles.qpReleaseTransfer",
      "requisition-transfer": "documentTypeConfig.workOrderRoles.requisitionTransfer",
      "requisition-return": "documentTypeConfig.workOrderRoles.requisitionReturn",
    };
    const role = roleOfRef(config, documentTypes);
    const fallbackKey = role ? fallbackKeys[role] : undefined;
    if (fallbackKey) return t(fallbackKey);

    const configCode = currentDocTypeCode(config, documentTypes);
    return documentTypes.find((documentType) => documentType.code === configCode)?.name || configCode;
  };

  const prostDocumentActions = useRNProstDocumentActions({
    rnWorkOrder,
    razmerRows,
    finishedProductRows: subprocessItemRows,
    isSaving: isSaving || documentBusy,
    isDirty,
    hasUnsavedSuggestions,
    documentTypes,
    issueMaterial6421Razmer,
    issueMaterial6425Razmer,
    receiveFinishedProduct7200Prost,
    saveBeforeReceive: () => handleSaveIngredient(),
    showToast,
    t,
  });
  const prostIssueConfig = configuredDocumentButtons.find((config) => roleOfRef(config, documentTypes) === 'issue-materials');
  const prostReceiptConfig = configuredDocumentButtons.find((config) => roleOfRef(config, documentTypes) === 'receive-finished');
  const prostIssueGpConfig = configuredDocumentButtons.find((config) => roleOfRef(config, documentTypes) === 'issue-finished');

  const renderConfiguredDocumentButtons = (scope: "stage" | "workOrder", area: "razmer" | "materialno" | "reception") =>
    configuredDocumentButtons
      .filter((config) => config.buttonScope === scope)
      .map((config) => {
        const label = getConfiguredDocumentButtonLabel(config);
        const role = roleOfRef(config, documentTypes);
        if (!canMoveStock && role !== "sampling") return null;
        switch (role) {
          case "issue-materials":
            return area === "materialno" ? (
              <RNConfiguredDocumentActionButton key={currentDocTypeCode(config, documentTypes)} icon={PackageMinus} label={label} title={materialno6421Title} disabled={materialno6421Disabled} onClick={handleIssueMaterial6421} />
            ) : null;
          case "issue-intermediate":
          case "receive-finished": {
            const isIssue = role === "issue-intermediate";
            if ((isIssue && area !== "materialno") || (!isIssue && (area !== "reception" || !isLastStage))) return null;
            const prefix = `os.materialno.${isIssue ? "issue6422" : "receive7200"}`;
            const rows = isIssue ? materialno6422EligibleRows : finishedProduct7200EligibleRows;
            const hasUnverified = isIssue ? materialno6422HasUnverified : finishedProduct7200HasUnverified;
            const quarantineRows = new Set(rnWorkOrder?.quarantineIntermediateRowKeys || []);
            // Medicines only: the lot waits for the QC lab release decision (after analysis).
            // Without the classification flag the quarantine never blocks 6422.
            const releasedRows = new Set(rnWorkOrder?.qcReleasedIntermediateRowKeys || []);
            const rejectedRows = new Set(rnWorkOrder?.qcRejectedIntermediateRowKeys || []);
            const quarantinedIssueRows = isIssue && rnWorkOrder?.intermediateRequiresQcRelease
              ? rows.filter((row) => quarantineRows.has(row.rowKey))
              : [];
            const qcRejected = quarantinedIssueRows.some((row) => rejectedRows.has(row.rowKey));
            const qcReleaseRequired = quarantinedIssueRows.some((row) => !releasedRows.has(row.rowKey));
            const existing = isIssue
              ? rnWorkOrder?.materialnoIssues?.find((entry) => entry.subprocessId === activeStage && entryHasCode(entry, currentDocTypeCode(config, documentTypes), documentTypes))
              : rnWorkOrder?.interProductReceipts?.find((entry) => entry.subprocessId === activeStage && entryHasCode(entry, currentDocTypeCode(config, documentTypes), documentTypes));
            // 6422 (issue-intermediate) is issuance — real edits only. 7200 (receive-finished)
            // is a receipt — also blocked by an unsaved batch/expiry suggestion (rule above).
            const reason = existing ? (isIssue ? "AlreadyIssued" : "AlreadyReceived")
              : !rnWorkOrder?._id ? "Unsaved" : isDirty ? "Dirty"
                : !isIssue && hasUnsavedSuggestions ? "Suggestions"
                : isSaving || documentBusy ? "Saving" : hasUnverified ? "NotVerified" : rows.length === 0 ? "NoRows"
                  : qcRejected ? "QcRejected" : qcReleaseRequired ? "QcReleaseRequired" : "Tooltip";
            const disabled = reason !== "Tooltip";
            return (
              <RNConfiguredDocumentActionButton
                key={currentDocTypeCode(config, documentTypes)}
                icon={isIssue ? PackageMinus : PackagePlus}
                label={label}
                title={t(`${prefix}${reason}`, { number: existing?.documentNumber || "" })}
                disabled={disabled}
                onClick={async () => {
                  if (disabled || !rnWorkOrder?._id || !activeStage) return;
                  setPendingDocument(role);
                  try {
                    const result = isIssue
                      ? await issueMaterial6422(rnWorkOrder._id, activeStage, currentDocTypeCode(config, documentTypes), rows)
                      : await receiveFinishedProduct7200(rnWorkOrder._id, activeStage);
                    showToast(t(`${prefix}Success`, { number: result.document?.number || "" }), "success");
                  } catch (err: any) {
                    const data = err?.response?.data;
                    showToast(formatRNDocumentError(data, t, `${prefix}Failed`), "error");
                  } finally {
                    setPendingDocument(null);
                  }
                }}
              />
            );
          }
          case "waste":
          case "scrap": {
            if (area !== "razmer" && area !== "materialno") return null;
            const action = getConfiguredAction(currentDocTypeCode(config, documentTypes), role, area);
            return (
              <RNConfiguredDocumentActionButton
                key={currentDocTypeCode(config, documentTypes)}
                icon={role === "scrap" ? ArrowRightLeft : PackageMinus}
                label={label}
                title={action.title}
                disabled={action.disabled}
                onClick={action.onClick}
              />
            );
          }
          case "sampling": {
            if (scope !== "workOrder" || area !== "materialno") return null;
            const disabled = isSaving || documentBusy || isDirty || !rnWorkOrder?._id;
            const title = !rnWorkOrder?._id
              ? t("rnUzurkuvanjeRequests.saveFirst")
              : isDirty
                ? t("os.materialno.issue6421Dirty")
                : isSaving || documentBusy
                  ? t("os.materialno.issue6421Saving")
                  : t("rnUzurkuvanjeRequests.openButton");
            return (
              <RNConfiguredDocumentActionButton
                key={currentDocTypeCode(config, documentTypes)}
                icon={PackageMinus}
                label={label}
                title={title}
                disabled={disabled}
                onClick={async () => {
                  if (disabled || !rnWorkOrder?._id) return;
                  setPendingDocument("sampling");
                  try {
                    const result = await issueSampling6424(rnWorkOrder._id);
                    showToast(t("documentTypeConfig.materialDocumentCreated", { number: result.document?.number || "" }), "success");
                  } catch (err: any) {
                    const data = err?.response?.data;
                    showToast(formatRNDocumentError(data, t, "documentTypeConfig.materialDocumentFailed"), "error");
                  } finally {
                    setPendingDocument(null);
                  }
                }}
              />
            );
          }
          case "receive-intermediate":
            return area === "reception" && !isLastStage ? (
              <RNConfiguredDocumentActionButton key={currentDocTypeCode(config, documentTypes)} icon={PackagePlus} label={label} title={interProduct7201Title} disabled={interProduct7201Disabled} onClick={handleReceiveMaterial7201} />
            ) : null;
          default:
            return null;
        }
      });
  const transferConfigured = !!documentTypes.find((type) => type.code === rnDocumentCode)?.transferCreations?.some((rule) => {
    const materialDocumentId = typeof rule.materialDocument === 'object' ? rule.materialDocument._id : rule.materialDocument;
    return documentTypes.find((type) => type._id === materialDocumentId)?.workOrderRole !== 'scrap';
  });
  const scrapTransferCodes = new Set(configuredDocumentButtons
    .filter((config) => roleOfRef(config, documentTypes) === 'scrap')
    .map((config) => currentDocTypeCode(config, documentTypes)));
  const hasTransferRows = materialnoGroups.some((group) => group.subprocessId === activeStage &&
    group.items.some((row) => row.confirmed && !!row.measuredQty?.trim() && isRowVerified(row)));
  const hasUnverifiedTransferRows = materialnoGroups.some((group) => group.subprocessId === activeStage &&
    group.items.some((row) => row.confirmed && !!row.measuredQty?.trim() && !isRowVerified(row)));
  const existingTransfer = (rnWorkOrder?.materialTransfers || []).find((entry) => entry.subprocessId === activeStage
    && !scrapTransferCodes.has(currentDocTypeCode(entry, documentTypes))
    && !entry.documents.some((document) => typeof document === 'object' && scrapTransferCodes.has(document.code || '')));
  const transferNumbers = (existingTransfer?.documents || [])
    .map((doc) => (typeof doc === 'object' && doc && 'number' in doc ? doc.number : null))
    .filter(Boolean)
    .join(', ');
  const transferReason = existingTransfer ? 'AlreadyCreated'
    : !rnWorkOrder?._id ? 'Unsaved'
    : isDirty ? 'Dirty'
    : isSaving || documentBusy ? 'Saving'
    : hasUnverifiedTransferRows ? 'NotVerified'
    : !hasTransferRows ? 'NoRows'
    : 'Tooltip';
  const transferDisabled = transferReason !== 'Tooltip';

  const renderTransferButton = () => canMoveStock && transferConfigured && hasWrite("WORK_ORDERS") && hasWrite("MATERIAL_DOCUMENTS") ? (
    <button
      type="button"
      disabled={transferDisabled}
      title={t(`documentTypeConfig.transfer${transferReason}`, { numbers: transferNumbers })}
      aria-label={t("documentTypeConfig.createTransfer")}
      className={`group relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg shadow-md transition-all duration-200 focus:outline-none focus:ring-2 text-[11px] font-semibold bg-white ${
        transferDisabled
          ? "border border-gray-300 text-gray-400 cursor-not-allowed"
          : "border-2 border-blue-600 text-blue-600 hover:bg-blue-50 hover:shadow-lg hover:scale-105 focus:ring-blue-500/50"
      }`}
      onClick={async () => {
        if (transferDisabled || !rnWorkOrder?._id || !activeStage) return;
        setPendingDocument('transfer');
        try {
          const result = await transferMaterial(rnWorkOrder._id, activeStage);
          showToast(t("documentTypeConfig.transferCreated", { numbers: result.documents.map((doc) => doc.number).join(", ") }), "success");
        } catch (err: any) {
          const data = err.response?.data;
          showToast(formatRNDocumentError(data, t, "workOrderTransfer.failed"), "error");
        } finally {
          setPendingDocument(null);
        }
      }}
    >
      <ArrowRightLeft size={14} strokeWidth={2.5} />
      {t("documentTypeConfig.createTransfer")}
    </button>
  ) : null;
  const isLastStage = subprocessItemRows.some((row) =>
    row.rowId?.startsWith('finished:') && row.subprocessId === activeStage);

  const materialno6422EligibleRows = React.useMemo(() =>
    materialnoGroups.filter((group) => group.subprocessId === activeStage)
      .flatMap((group) => group.items)
      .filter((row) => row.osRowId && row.itemId && isIntermediateOrSemi({ group: row.accountingTypeGroup })
        && row.confirmed && isRowVerified(row) && (parseDecimalInput(row.measuredQty) ?? 0) > 0)
      .map((row) => ({ rowKey: row.osRowId!, item: row.itemId, ident: row.ident, name: row.name, unitCode: row.unitCode })),
  [activeStage, materialnoGroups]);

  const materialno6422HasUnverified = React.useMemo(() =>
    materialnoGroups.filter((group) => group.subprocessId === activeStage)
      .flatMap((group) => group.items)
      .some((row) => row.osRowId && row.itemId && isIntermediateOrSemi({ group: row.accountingTypeGroup })
        && row.confirmed && !isRowVerified(row) && (parseDecimalInput(row.measuredQty) ?? 0) > 0),
  [activeStage, materialnoGroups]);

  const finishedProduct7200EligibleRows = React.useMemo(() =>
    visibleInterProductRows.filter((row) => row.rowId?.startsWith('finished:')
      && isFinishedProduct({ group: row.accountingTypeGroup }) && row.confirmed && isRowVerified(row) && (parseDecimalInput(row.measuredQty) ?? 0) > 0)
      .map((row) => ({ rowKey: row.rowId!, item: row.itemId, ident: row.ident, name: row.name, unitCode: row.unitCode })),
  [visibleInterProductRows]);

  const finishedProduct7200HasUnverified = React.useMemo(() =>
    visibleInterProductRows.some((row) => row.rowId?.startsWith('finished:')
      && isFinishedProduct({ group: row.accountingTypeGroup }) && row.confirmed && !isRowVerified(row)
      && (parseDecimalInput(row.measuredQty) ?? 0) > 0),
  [visibleInterProductRows]);

  const renderDispatchButton = (stage: RNRazmerDispatchStage, label: string, ready: boolean) => {
    // Enable кога сите eligible редови од секцијата се потврдени.
    // Не бара isDirty=false — потврдата авто-зачувува; isDirty би го држел копчето locked.
    const disabled = isSaving || isDispatching || !rnWorkOrder?._id || !ready;
    const title = !rnWorkOrder?._id
      ? "Прво зачувај го работниот налог"
      : !ready
        ? "Сите редови мора да се потврдени (✓) и проверени (Проверил) пред испраќање"
        : label;
    return (
      <button
        type="button"
        onClick={() => openDispatchModal(stage)}
        disabled={disabled}
        title={title}
        className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"
            : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        }`}
      >
        <Send size={14} />
        {label}
      </button>
    );
  };

  // Keep the full-page loader until both the linked OS ingredient and the RN
  // actuals are ready. Rendering the shell between these requests exposes
  // partially populated fields and causes a visible layout flash.
  const ingredientSyncPending = !!ingredient && ingredient._id !== lastSyncedIngredientId;
  if (isInitialHydrating || loadingIngredient || rnLoading || ingredientSyncPending || isBlockingSave || isDeleting) {
    return <LoadingSpinner />;
  }
  if (errorList) return <ErrorComponent message={errorList} />;

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <div className="flex-1 flex flex-col transition-all duration-300"
        style={{
          paddingLeft: '0',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          backgroundColor: 'var(--color-background)'
        }}>
        <div className="w-full flex-shrink-0">
          <Navbar />
        </div>
        <div className="flex-1 flex flex-col overflow-x-hidden py-1 px-4 mt-12">
          <ShortcutNavBar />
          <RNMasterHeader
            isCreating={isCreating}
            isSaving={isSaving}
            hasChanges={canSaveRN}
            onReset={resetForm}
            onSave={handleSaveIngredient}
            onDelete={handleDeleteRequest}
            deleteDisabled={!rnWorkOrder?._id}
            deleteDisabledTitle={!rnWorkOrder?._id ? 'Нема зачуван работен налог за бришење' : undefined}
            // No onArchive — RN must never archive the OS Ingredient.
            hasIngredient={!!(rnWorkOrder?._id || ingredient?._id)}
            search={rnSearch}
            setSearch={setRnSearch}
            rows={rnList}
            loading={rnListLoading}
            page={rnPage}
            totalPages={rnTotalPages}
            total={rnTotal}
            absoluteTotal={rnAbsoluteTotal}
            setPage={setRnPage}
            ingredientCode={selectedItemCode || undefined}
            ingredientName={(ingredient?.items && typeof ingredient.items === 'object' ? ingredient.items.name : undefined) || selectedItemName || undefined}
            currentIngredientId={rnWorkOrder?._id || undefined}
            currentIngredientCode={rnWorkOrder?.number || selectedItemCode || undefined}
            onModalOpen={reloadRnList}
            onPrintPdf={handlePrintPdf}
            renderExcelExport={() => (
              <ExcelExportDropdown
                onDownloadAll={handleExportAll}
                onDownloadCurrent={handleExportCurrent}
                onDownloadFiltered={() => setShowExportModal(true)}
                hasCurrentItem={!!ingredient?._id}
                currentItemCode={selectedItemCode || undefined}
                isDisabled={isExporting}
                entityLabel={t('sidebar.osBasicSpecification') || 'Basic Specification'}
              />
            )}
            onIngredientSelect={(row: RNWorkOrderRow) => {
              if (isDirty || hasUnsavedSuggestions) {
                pendingRnRowRef.current = row;
                setShowUnsavedModal(true);
                return;
              }
              loadRnRow(row);
            }}
          />

          <OSDetailHeader
            mode="rn"
            allowedSubprocessIds={allowedSubprocessIds}
            // Исто како нормативот: процесите десно под Податоци, подпроцесите во еден ред.
            stageTreeSlot={isSimpleVariant ? undefined : (
              <OSStageTree
                allowedSubprocessIds={allowedSubprocessIds}
                notice={hasNoAssignedStages ? t("os.stageTree.noAssignedSubprocesses") : undefined}
                activeStage={activeStage}
                onSelect={setActiveStage}
                filterSubprocessIds={selectedSubprocessIds}
                showSubprocessStatus
                razmerStatusLabel={stageStatusLabel}
                layout="stacked"
              />
            )}
            selectedItemId={selectedItemId}
            selectedItemCode={selectedItemCode}
            selectedItemName={(ingredient?.items && typeof ingredient.items === 'object' ? ingredient.items.name : undefined) || selectedItemName}
            onItemChange={(v) => { clearMachineRepairs(); setSelectedRnId(null); setSelectedItemId(v); setIsDirty(true); }}
            batch={batch}
            onBatchChange={setBatch}
            batchUnit={batchUnit}
            batchUnitCode={batchUnitCode}
            waste={waste}
            onWasteChange={setWaste}
            plannedWaste={plannedWaste}
            onPlannedWasteChange={setPlannedWaste}
            validFrom={validFrom}
            onValidFromChange={setValidFrom}
            validTo={validTo}
            onValidToChange={setValidTo}
            workOrderNumber={rnWorkOrder?.number || ""}
            rnWorkOrderId={rnWorkOrder?._id}
            versionDisplay={versionDisplay}
            productionCode={productionCode}
            onProductionCodeChange={setProductionCode}
            statusFields={statusFields}
            statusValue={selectedStatus}
            onStatusChange={(id) => { setSelectedStatus(id); setIsDirty(true); }}
            isEditable={true}
            showValidationErrors={showValidationErrors}
            subprocessItemRows={subprocessItemRows}
            onSubprocessItemRowsChange={(rows, markDirty = true) => {
              setSubprocessItemRows(rows);
              setInterProductActuals(toInterProductActualRows(rows));
              if (markDirty) setIsDirty(true);
            }}
            isCreating={isCreating}
            isAdmin={isAdmin}
            preparedUserName={rnWorkOrder?.prepared?.userName}
            preparedDateDisplay={rnWorkOrder?.prepared?.date ? moment(rnWorkOrder.prepared.date).format("DD.MM.YYYY") : ""}
            workOrderDateDisplay={rnWorkOrder?.createdAt ? moment(rnWorkOrder.createdAt).format("DD.MM.YYYY") : ""}
            productionPlanNumber={rnWorkOrder?.productionPlanNumber || ""}
            plannedStartDateDisplay={rnWorkOrder?.plannedStartDate ? moment(rnWorkOrder.plannedStartDate).format("DD.MM.YYYY") : ""}
            productionDateDisplay={rnWorkOrder?.productionDate ? moment(rnWorkOrder.productionDate).format("DD.MM.YYYY") : ""}
            finishedProductLocked={(rnWorkOrder?.interProductReceipts || []).some((receipt) => receiptEntryRole(receipt, documentTypes) === 'receive-finished')}
            onResetPrepared={handleResetPrepared}
            approvedUserName={rnWorkOrder?.approved?.userName}
            approvedDateDisplay={rnWorkOrder?.approved?.date ? moment(rnWorkOrder.approved.date).format("DD.MM.YYYY") : ""}
            approvedSignature={rnWorkOrder?.approved?.signature}
            onSignApproved={() => setSignField("approved")}
            onRemoveApprovedSignature={handleRemoveApproved}
            hideSubprocessItemsTable={isSimpleVariant}
            hideDetailData={isSimpleVariant}
            actionsSlot={
              isSimpleVariant ? undefined : (
                <>
                  <button
                    type="button"
                    onClick={() => setShowTotalProductivity((value) => !value)}
                    title={showTotalProductivity ? t('os.hideTotalProductivity') : t('os.showTotalProductivity')}
                    className={`${OS_CARD_HEADER_BUTTON_CLS} ${showTotalProductivity ? OS_CARD_HEADER_BUTTON_ACTIVE_CLS : ''}`}
                  >
                    Log
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowStatusLog((value) => !value)}
                    title={showStatusLog ? t('os.hideStatusLog') : t('os.showStatusLog')}
                    className={`${OS_CARD_HEADER_BUTTON_CLS} ${showStatusLog ? OS_CARD_HEADER_BUTTON_ACTIVE_CLS : ''}`}
                  >
                    Статус
                  </button>
                </>
              )
            }
          />

          <div className="hidden">
            {renderDispatchButton("preparation", razmerDispatchLabels.preparation, preparationDispatchState.ready)}
            {renderDispatchButton("packaging", razmerDispatchLabels.packaging, packagingDispatchState.ready)}
          </div>

          {!isSimpleVariant && showTotalProductivity && (
            <>
              <OSProductivityTable
                mode="rn"
                title={t('os.totalProductivity')}
                rows={allowedSubprocessIds
                  ? totalProductivityRows.filter((r) => r.subprocessId === RAZMER_STAGE_ID || allowedSubprocessIds.has(r.subprocessId || ''))
                  : totalProductivityRows}
                onChange={() => {}}
                readOnly
                readOnlyMachineSelect
                groupBySubprocess
                subprocessNameById={subprocessNameById}
              />
            </>
          )}

          {!isSimpleVariant && showStatusLog && (
            <RNStatusLogTable workOrderId={rnWorkOrder?._id} />
          )}

          {configuredDocumentButtons.some((config) => config.buttonScope === "workOrder") && (
            <div className="mb-3 flex flex-wrap gap-2">
              {renderConfiguredDocumentButtons("workOrder", "razmer")}
              {!isSimpleVariant && renderConfiguredDocumentButtons("workOrder", "materialno")}
              {!isSimpleVariant && renderConfiguredDocumentButtons("workOrder", "reception")}
            </div>
          )}

          <div className="space-y-4" onBlurCapture={stageAutosave.onBlurCapture}>
            <AutosaveStatusPill status={stageAutosave.status} />
            <OSStagePanel
              mode="rn"
              hideProductivity={isSimpleVariant}
              razmerColumnManager={isSimpleVariant}
              razmerLotSuggestions={razmerLotSuggestions}
              razmerHiddenColumns={razmerHiddenColumns}
              onRazmerColumnToggle={handleRazmerColumnToggle}
              onRazmerColumnDrop={handleRazmerColumnDrop}
              isRazmerStage={isRazmerStage}
              razmerIsSaving={isSaving}
              productivityRows={visibleProdRows}
              onProductivityChange={(rows) => {
                if (!activeStage) return;
                setProductivityRows((prev) => [
                  ...prev.filter((r) => r.subprocessId !== activeStage),
                  ...rows.map((r) => ({ ...r, subprocessId: activeStage })),
                ]);
                setIsDirty(true);
              }}
              productivityMachineIds={activeStageMachineIds}
              groupProductivityBySubprocess
              subprocessNameById={subprocessNameById}
              editableProductivitySummary
              onMontageStartCommit={selectedItemId && rnWorkOrder?._id && (isRazmerStage ? productionStatusOptions[0] : activeConfiguredStatuses[0])
                ? () => {
                    const status: any = isRazmerStage ? productionStatusOptions[0] : activeConfiguredStatuses[0];
                    setRazmerStartedStatus({ itemId: selectedItemId, workOrderId: rnWorkOrder._id, statusId: status._id, statusIndex: 0, stageId: activeStage });
                    saveAfterTimingCommit();
                  }
                : undefined}
              onMontageStartClear={() => setRazmerStartedStatus(null)}
              onMontageEndCommit={selectedItemId && rnWorkOrder?._id && (isRazmerStage ? productionStatusOptions[1] : activeConfiguredStatuses[1]) && razmerStartedStatus?.stageId === activeStage && razmerStartedStatus?.statusIndex === 0
                ? () => { const status: any = isRazmerStage ? productionStatusOptions[1] : activeConfiguredStatuses[1]; setRazmerStartedStatus({ itemId: selectedItemId, workOrderId: rnWorkOrder._id, statusId: status._id, statusIndex: 1, stageId: activeStage }); saveAfterTimingCommit(); }
                : undefined}
              onMontageEndClear={() => setRazmerStartedStatus((prev) => { const first: any = isRazmerStage ? productionStatusOptions[0] : activeConfiguredStatuses[0]; return prev?.statusIndex === 1 && first
                ? { ...prev, statusId: first._id, statusIndex: 0 }
                : prev; })}
              onTimingCommit={selectedItemId && rnWorkOrder?._id ? (field: any) => {
                const indexByField: Record<string, number> = { machineStart: 2, machineEnd: 3, dismountStart: 4, dismountEnd: 5, cleaningStart: 6, cleaningEnd: 7 };
                const nextIndex = indexByField[field];
                const nextStatus: any = isRazmerStage ? productionStatusOptions[nextIndex] : activeConfiguredStatuses[nextIndex];
                if (nextIndex == null || !nextStatus) return;
                setRazmerStartedStatus((prev) => prev && prev.stageId === activeStage && prev.statusIndex === nextIndex - 1
                  ? { ...prev, statusId: nextStatus._id, statusIndex: nextIndex, stageId: activeStage }
                  : prev);
                saveAfterTimingCommit();
              } : undefined}
              razmerRows={visibleRazmerRows}
              onRazmerChange={(rows) => {
                setRazmerRows(rows);
                setRazmerActuals(toRazmerActualRows(rows));
                setIsDirty(true);
              }}
              onRazmerChangeAndSave={handleRazmerChangeAndSave}
              onRazmerPreviewChange={handleRazmerPreviewChange}
              razmerCellSaveStatus={razmerCellSaveStatus}
              onRazmerActualCommit={razmerAutosave.commit}
              razmerToolbarSlot={
                isSimpleVariant ? (
                  <>
                    {canMoveStock && (
                      <RNConfiguredDocumentActionButton
                        icon={PackageMinus}
                        label={prostIssueConfig ? getConfiguredDocumentButtonLabel(prostIssueConfig) : t('os.materialno.issue6421')}
                        title={prostDocumentActions.issueTitle}
                        disabled={prostDocumentActions.issueDisabled}
                        onClick={prostDocumentActions.handleIssue}
                      />
                    )}
                    {canMoveStock && (
                      <RNConfiguredDocumentActionButton
                        icon={PackagePlus}
                        label={prostReceiptConfig ? getConfiguredDocumentButtonLabel(prostReceiptConfig) : t('os.materialno.receive7200')}
                        title={prostDocumentActions.receiveTitle}
                        disabled={prostDocumentActions.receiveDisabled}
                        onClick={prostDocumentActions.handleReceive}
                      />
                    )}
                    {/* Shown only when the 6010 matrix still routes finished products to a
                        separate issue-finished document; otherwise „Издади" covers them. */}
                    {canMoveStock && prostIssueGpConfig && (
                      <RNConfiguredDocumentActionButton
                        icon={PackageMinus}
                        label={getConfiguredDocumentButtonLabel(prostIssueGpConfig)}
                        title={prostDocumentActions.issueGpTitle}
                        disabled={prostDocumentActions.issueGpDisabled}
                        onClick={prostDocumentActions.handleIssueGp}
                      />
                    )}
                    {renderConfiguredDocumentButtons("stage", "razmer")}
                  </>
                ) : (
                  <>
                    {renderDispatchButton("preparation", razmerDispatchLabels.preparation, preparationDispatchState.ready)}
                    {renderDispatchButton("packaging", razmerDispatchLabels.packaging, packagingDispatchState.ready)}
                    {renderConfiguredDocumentButtons("stage", "razmer")}
                  </>
                )
              }
              onMaterialnoChangeAndSave={handleMaterialnoChangeAndSave}
              materialnoIsSaving={isSaving}
              materialnoToolbarSlot={!isRazmerStage ? <>{renderConfiguredDocumentButtons("stage", "materialno")}{renderTransferButton()}</> : undefined}
              materialnoGroups={visibleMaterialnoGroups}
              materialnoAllowCopyFromRazmer={materialnoAllowCopyFromRazmer}
              materialnoRazmerCopySourceByItemId={materialnoRazmerCopySourceByItemId}
              materialnoLockUnsavedRows={!rnWorkOrder?.normativSnapshot}
              onMaterialnoChange={(g) => {
                const nextGroups = mergeMaterialnoStageGroups(materialnoGroups, g);
                setMaterialnoGroups(nextGroups);
                setMaterialnoActuals(toMaterialnoActualRows(nextGroups));
                setIsDirty(true);
              }}
              interProductRows={visibleInterProductRows}
              interProductIsSaving={isSaving}
              interProductToolbarSlot={!isRazmerStage ? <>{renderConfiguredDocumentButtons("stage", "reception")}</> : undefined}
              rnWorkOrderId={rnWorkOrder?._id}
              rnWorkOrderNumber={rnWorkOrder?.number || ''}
              interProductReceipts={rnWorkOrder?.interProductReceipts}
              onSamplingApproval={() => {
                if (rnWorkOrder?._id && !isDirty) void loadRNById(rnWorkOrder._id);
              }}
              onInterProductChangeAndSave={handleInterProductChangeAndSave}
              onInterProductChange={(rows) => {
                const next = mergeInterProductStageRows(subprocessItemRows, rows);
                setSubprocessItemRows(next);
                setInterProductActuals(toInterProductActualRows(next));
                setIsDirty(true);
              }}
              activeSubprocess={activeSubprocess}
              machineRepairsSlot={
                !isRazmerStage ? (
                  <>
                    <RNSubprocessMachinesTable
                    collapsed={!showMachineRepairs}
                    onToggleCollapsed={() => setShowMachineRepairs((value) => !value)}
                    activeSubprocess={machineTableSubprocess}
                    repairs={machineRepairs}
                    onChange={(subprocessId, machineId, field, value) => {
                      updateMachineRepair(subprocessId, machineId, field, value);
                      setIsDirty(true);
                    }}
                    onApprove={handleApproveMachineRepair}
                    approvingKey={approvingKey}
                    dirty={isDirty}
                    readOnly={rnLoading || isSaving || !hasWrite('WORK_ORDERS')}
                    canApprove={canApproveMachineRepairs}
                    rnWorkOrderId={rnWorkOrder?._id}
                    rnWorkOrderNumber={rnWorkOrder?.number || ''}
                    initialSubprocessId={machineRepairNavigation?.subprocessId}
                    initialMachineId={machineRepairNavigation?.machineId}
                    initialRepairRequestId={machineRepairNavigation?.machineRepairRequestId}
                    />
                  </>
                ) : undefined
              }
              inProcessValues={inProcessValues}
              onInProcessChange={(entryId, value) => { setInProcessValues((prev) => ({ ...prev, [entryId]: value })); setIsDirty(true); }}
              montageValues={montageValues}
              onMontageChange={(entryId, value) => { setMontageValues((prev) => ({ ...prev, [entryId]: value })); setIsDirty(true); }}
            />
          </div>
        </div>
      </div>

      <ModalConfirmation
        isOpen={confirmDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        description={rnWorkOrder?.number || selectedItemCode || ""}
        title="Избриши работен налог"
        note="Ќе се избрише само RN документот/actuals. OS рецептот нема да се избрише."
      />

      <RNExportFilterModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExportFiltered}
        isExporting={isExporting}
        currentSearch={search}
      />

      <UnsavedChangesModal
        show={showUnsavedModal}
        onCancel={handleUnsavedCancel}
        onConfirm={handleUnsavedConfirm}
      />

      <ModalSignPin
        isOpen={!!signField}
        onClose={() => setSignField(null)}
        onConfirm={handleSign}
        fieldLabel={signField ? (t(`qualDoc.${signField}`) || signField) : ""}
      />

      <RNRazmerDispatchModal
        isOpen={!!dispatchModalStage}
        stage={dispatchModalStage || "preparation"}
        title={dispatchModalStage ? razmerDispatchLabels[dispatchModalStage] : undefined}
        workOrderNumber={rnWorkOrder?.number || ""}
        senderName={dispatchSenderName}
        rows={dispatchModalRows}
        users={usersList}
        selectedUserId={dispatchUserId}
        isSubmitting={isDispatching}
        onUserChange={setDispatchUserId}
        onClose={() => { if (!isDispatching) { setDispatchModalStage(null); } }}
        onSubmit={handleDispatchSubmit}
      />

      <RNRasturStageModal
        isOpen={rasturModalOpen}
        stages={rastur6423StageOptions.map((stage) => ({
          subprocessId: stage.subprocessId,
          name: stage.name,
          rowCount: stage.rows.length,
          existingDocumentNumber: stage.existingDocumentNumber,
        }))}
        isSubmitting={isIssuingRastur6423}
        onSelect={handleSelectRasturStage}
        onClose={() => { if (!isIssuingRastur6423) setRasturModalOpen(false); }}
      />

      <RNRasturStageModal
        isOpen={configuredPickerOpen}
        title={configuredPickerTitle}
        stages={configuredStageOptions}
        isSubmitting={pendingConfiguredDocument}
        onSelect={selectConfiguredStage}
        onClose={() => { if (!pendingConfiguredDocument) setConfiguredPickerOpen(false); }}
      />
    </div>
  );
};

// Wrapper component to provide ItemCacheContext
const RNWorkOrder: React.FC<RNWorkOrderInnerProps> = ({ variant }) => {
  return (
    <ItemCacheProvider>
      <RNWorkOrderInner variant={variant} />
    </ItemCacheProvider>
  );
};

export default RNWorkOrder;
